import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Polyline, Popup, Tooltip as MapTooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import icon2x from 'leaflet/dist/images/marker-icon-2x.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';
import PanelVozila from './PanelVozila';
import PanelAlarmi from './PanelAlarmi';
import DialogAlarmi from './DialogAlarmi';
import PretragaGlavna from './PretragaGlavna';
import DialogPodesavanjaPretrage from './DialogPodesavanjaPretrage';
import DialogVozilaUBlizini, { BOJE_RUTA } from './DialogVozilaUBlizini';
import DialogLokacija from './DialogLokacija';
import PanelMerenje from './PanelMerenje';
import PopupPin from './PopupPin';
import { IZVOR, PODRAZUMEVANO, upakuj, raspakuj } from './pretragaKonfig';
import { googleLinkPrikaza, googleLinkRute, googleLinkTacke, otvoriGoogle,
         uzorakTrase, kopirajLink } from './googleLinkovi';
import PopupStajanje from './PopupStajanje';
import { tackeStrelica, IKONA_STRELICA, ikonaStajanja, trajanjeTeksta,
         IKONA_NEVIDLJIVA, ikonaTrase, ikonaOdredista } from './stajanja';
import PopupSegment from './PopupSegment';
import { nadjiSegment, opisSegmenta, duzinaTeksta } from './segmentRute';
import PopupIncident from './PopupIncident';
import DialogPlanRute from './DialogPlanRute';
import { PULS_NAJVISE, TRAGOVI_DEONICE, veziOblacic, vrstaOpis, ikonaIncidenta } from './incidenti';
import { SLOJEVI, nadjiSloj, imaSaobracaj, upakujMapu, raspakujMapu } from './slojeviMape';
import SlojTacaka, { kljucTipa, izgledTipa } from './tackeMape';
import usePermission from '../../custom/usePermission';
import { AuthContext } from '../../Header/auth - context';
import './GPS.css';

// CRA ne resolvuje default marker putanje sam — slike idu iz leaflet paketa (bez CDN-a).
L.Icon.Default.mergeOptions({
    iconUrl: icon,
    iconRetinaUrl: icon2x,
    shadowUrl: iconShadow,
});

const CENTAR_SRBIJA = [44.7866, 20.4489];
const OSVEZAVANJE_MS = 30000;      // trazeno: mapa se sama osvezava na 30s
const TRAG_MINUTA = 60;            // trag iza svakog vozila (1h)
const RUTA_SATI = 48;              // cela ruta izabranog vozila

/* Alarmi ne stoje stalno nego KRUŽE: minut se vide, pet minuta miruju, pa opet.
   Tako dispečer bude podsećen, a mapa mu ostane slobodna najveći deo vremena.

   Minut je i zaštitno vreme: kad je uključeno više vrsta, one se smenjuju i
   nikad se ne prikazuju istovremeno, pa svaka stigne da bude pročitana. */
const ALARM_PRIKAZ_MS = 60 * 1000;
const ALARM_PAUZA_MS  = 5 * 60 * 1000;

/* Oznacavanje vozila NE ceka ciklus — crveno stoji sve vreme dok su alarmi
   ukljuceni, jer istekla saobracajna ne prestaje da bude problem izmedju dva
   prikaza. Ciklus i dalje vlada samo bocnim spiskom.
   Trepere samo ona kojima je dokument istekao ili istice u roku od nedelju
   dana; sto je dalje od toga, stoji u spisku ali ne vrsti oko sebe. */
const ALARM_PRAG_DANA = 7;
const ALARM_OSVEZAVANJE_MS = 10 * 60 * 1000;

/* Stanja po kojima se filtrira sa čipova u zaglavlju. Isti ključevi koje vraća
   stanjeVozila(), pa filter ne mora ništa da preslikava. */
const STANJA_VOZILA = [
    { kljuc: 'vozi',  naziv: 'gps.vozi' },
    { kljuc: 'stoji', naziv: 'gps.stoji' },
    { kljuc: 'staro', naziv: 'gps.bezSignala' },
];

/* Vrste alarma koje mogu da kruže. Vozači i fakture su predviđeni ali još nisu
   napravljeni — stoje u spisku onemogućeni. Kad se naprave, dovoljno je
   "dostupno: true" i grana u ciklusu koja im učitava podatke. */
const VRSTE_ALARMA = [
    { kljuc: 'vozila',  naziv: 'gps.alarmiVozila',  opis: 'gps.alarmiVozilaOpis',  ikona: 'pi pi-truck',     dostupno: true  },
    { kljuc: 'vozaci',  naziv: 'gps.alarmiVozaca',  opis: 'gps.alarmiVozacaOpis',  ikona: 'pi pi-id-card',   dostupno: false },
    { kljuc: 'fakture', naziv: 'gps.alarmiFaktura', opis: 'gps.alarmiFakturaOpis', ikona: 'pi pi-file-edit', dostupno: false },
];

/* Vozilo se boji po tome da li se krece, stoji ili se dugo nije javilo.
   Prag od 15 minuta je izabran jer uredjaj salje na svakih par minuta —
   duza tisina znaci da vozilo nije van dometa nego da veza ne radi. */
const MINUTA_ZASTARELO = 15;

const stanjeVozila = (v) => {
    if ((v.minuta_staro ?? 0) > MINUTA_ZASTARELO) return 'staro';
    return v.u_pokretu ? 'vozi' : 'stoji';
};

/* Oblici se crtaju iz profila — prepoznatljivi i na malom zumu, za razliku od
   pogleda odozgo koji se na 20px svede na mrlju. Sve u jednoj boji
   (currentColor), da ih stanje vozila oboji bez diranja putanja.

   Pola voznog parka su prikolice: ne smeju izgledati kao kamion, jer se na mapi
   ne bi razlikovalo vuce li nesto samo od sebe ili stoji otkaceno. */

// Vozilo BEZ prepoznate sprege — obican kamion, kabina napred i tri osovine.
//
// Namerno NIJE tegljac: sprega se cita iz putnog naloga, pa kad naloga nema ne
// znamo vuce li vozilo nesto. Sam tegljac (kabina i sedlo, bez sanduka) tvrdi
// da je otkacen, a to nije podatak koji imamo — obican kamion ne tvrdi nista
// preko onoga sto se zna: da je to teretno vozilo.
const SVG_KAMION = `
<svg viewBox="0 0 64 30" class="gps-kamion-svg" aria-hidden="true">
  <rect x="1"  y="5"  width="34" height="16" rx="2"/>
  <path d="M37 21 V10 h8 l7 7 v4 z"/>
  <circle cx="11" cy="23" r="4.2"/>
  <circle cx="28" cy="23" r="4.2"/>
  <circle cx="47" cy="23" r="4.2"/>
</svg>`;

// Tegljac (sleper), gledan zdesna. Crta se SAMO u sprezi — bez prikolice se
// koristi SVG_KAMION iznad.
//
// Duzina je uzeta iz stvarnih mera, ne odokativno: cela kompozicija je 16,5 m,
// tegljac 6,5 m — dakle 39% ukupne duzine. Kabina je 40% samog tegljaca,
// medjuosovinsko rastojanje 62%.
//
// Sasija je JEDAN potez sa stepenikom — visi deo pozadi je sedlo, nizi ide ka
// kabini. (Dva zasebna pravougaonika su probana i beli obrub izmedju njih je
// vozilo pretvorio u merdevine.)
//
// viewBox je 46 sirok prema 86 kod poluprikolice. Obe se crtaju na istu
// VISINU (26px), a posto obe imaju
// viewBox visok 30 i tockove na y=23, razmera im ispadne ista i tockovi legnu
// u istu liniju.
const SVG_VOZILO = `
<svg viewBox="0 0 46 30" class="gps-kamion-svg" aria-hidden="true">
  <path d="M27 20 V6 q0-2.4 2.4-2.4 H41.4 q2 0 2.5 1.9 L45.4 15.6 V20 z"/>
  <path d="M0 12.6 H15.3 V15.6 H27.6 V20 H0 Z"/>
  <circle cx="8.1" cy="23" r="4.2"/>
  <circle cx="36.9" cy="23" r="4.2"/>
</svg>`;

// Poluprikolica — JEDAN crtez i za samostalnu oznaku (hladnjaca sa uredjajem)
// i za onu u sprezi. Ranije su to bila dva razlicita crteza, okrenuta na
// suprotne strane, pa je isto vozilo izgledalo drugacije zavisno od toga da li
// je bilo zakaceno.
//
// Duza je od tegljaca (viewBox 86 prema 46). Tri osovine pozadi, noge iza
// sredine — po tome se cita da je poluprikolica, a ne prikolica.
const SVG_POLUPRIKOLICA = `
<svg viewBox="0 0 86 30" class="gps-kamion-svg" aria-hidden="true">
  <rect x="0.6" y="3" width="84.8" height="17" rx="1.8"/>
  <rect x="60.3" y="19" width="2.8" height="5.4" rx="1.4"/>
  <circle cx="11.4" cy="23" r="4.2"/>
  <circle cx="20.4" cy="23" r="4.2"/>
  <circle cx="29.4" cy="23" r="4.2"/>
</svg>`;

// Prikolica (sa rudom) — okrenuta na istok kao i sve ostalo, ruda napred desno,
// tockovi na oba kraja. Kraca je od poluprikolice (7,8 m prema 13,6 m), pa joj
// je i viewBox uzi u istoj razmeri.
const SVG_PRIKOLICA = `
<svg viewBox="0 0 55 30" class="gps-kamion-svg" aria-hidden="true">
  <rect x="0.6" y="3" width="48" height="17" rx="1.8"/>
  <rect x="47.7" y="12.6" width="6.8" height="3.4" rx="1.6"/>
  <circle cx="9.9" cy="23" r="4.2"/>
  <circle cx="39.6" cy="23" r="4.2"/>
</svg>`;

/* Dugme za saobracaj — dva automobila, jedan iza drugog, kao na velikim
   mapama. PrimeIcons nema ovakvu ikonu, a priblizne (strelice, put) ne
   znace „gustina saobracaja" nego nesto drugo, pa bi dispecer procitao
   pogresnu radnju. Crta se linijama, ne punim oblicima: na 18px puna silueta
   automobila se stopi u mrlju.

   Kola su namerno POMERENA dijagonalno, a ne jedna pored drugih — tako se i
   pri toj velicini cita kolona, sto je i smisao dugmeta. */
const IKONA_SAOBRACAJ = `
<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
     stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="M2.4 6.6 3.7 4.1a1.5 1.5 0 0 1 1.33-.8h6.14a1.5 1.5 0 0 1 1.33.8l1.3 2.5"/>
  <rect x="1.6" y="6.6" width="13.2" height="4.2" rx="1.3"/>
  <path d="M4.4 10.8v1M12 10.8v1"/>
  <path d="M9.2 17.4l1.3-2.5a1.5 1.5 0 0 1 1.33-.8h6.14a1.5 1.5 0 0 1 1.33.8l1.3 2.5"/>
  <rect x="8.4" y="17.4" width="13.2" height="4.2" rx="1.3"/>
  <path d="M11.2 21.6v1M18.8 21.6v1"/>
</svg>`;

const OBLIK = {
    POLUPRIKOLICA: SVG_POLUPRIKOLICA,
    PRIKOLICA:     SVG_PRIKOLICA,
};

/* Umesto rotacije kamion se PRESLIKAVA levo-desno. Rotiran profilni kamion bi
   se pri vracanju na zapad okrenuo naglavacke; preslikan i dalje tacno kaze
   kojom stranom sveta ide. Ugao 0 je sever, pa je zapadna polovina 180-360. */
const ideNaZapad = (pravac) => {
    const a = ((pravac ?? 0) % 360 + 360) % 360;
    return a > 180 && a < 360;
};

/* Registracije se porede bez razmaka i po velikim slovima: ista tablica ume da
   stoji kao "AB-649VS" u GPS zapisu i "AB-649 VS" u voznom parku. */
const golaReg = (r) => (r || '').replace(/\s+/g, '').toUpperCase();

/* Ona registracija iz para koja NIJE ovo vozilo — kod vuce je to prikolica, kod
   prikolice vuca. Null kad vozilo nije na nalogu ili je na njemu samo. */
const partnerVozila = (v) => {
    if (!v?.reg_vucno) return null;
    const svoja = golaReg(v.registracija);
    if (golaReg(v.reg_vucno) !== svoja) return v.reg_vucno;
    if (v.reg_prikljucno && golaReg(v.reg_prikljucno) !== svoja) return v.reg_prikljucno;
    return null;
};

/* Oznaka vozila se sklapa kao HTML string (Leaflet divIcon prima html, ne JSX),
   pa sve sto dolazi iz baze mora proci kroz escape — tablica sa < ili & bi
   inace razbila oznaku. */
const bezHtml = (t) => String(t ?? '').replace(/[&<>"]/g, z =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[z]));

/* nivo: null | 'isteklo' | 'uskoro'.
   Isteklo je crveno i treperi; ono sto istice u roku od nedelju dana samo
   treperi crvenim prstenom, da se dve hitnosti ne izjednace.
   Izabrano vozilo nema alarm — ono je izricit izbor korisnika i mora da ostane
   plavo, inace se ne vidi sta je izabrano. */
const klaseAlarma = (nivo, izabrano) =>
    izabrano || !nivo ? ''
    : nivo === 'isteklo' ? ' gps-alarmirano gps-alarm-treperi'
    : ' gps-alarm-uskoro gps-alarm-treperi';

/* uAlarmu / alarmPartnera: null | 'isteklo' | 'uskoro' — svako svoj, jer su to
   dva vozila i dokumenti im isticu nezavisno. */
const ikonaVozila = (v, izabrano, uAlarmu, alarmPartnera) => {
    const stanje = stanjeVozila(v);
    const naZapad = stanje === 'vozi' && ideNaZapad(v.pravac);
    const omot = `gps-vozilo gps-${stanje}${izabrano ? ' gps-izabrano' : ''}`;

    /* Sa koje sam strane sprege. Odlucuje oblik, poredak i supljinu: suplja se
       crta PRIKOLICA — supljina prati oblik, ne mesto u sprezi, pa ista
       prikolica izgleda isto i kad stoji sama i kad je zakacena. */
    const jaSamPrikolica = v.vrsta === 'POLUPRIKOLICA' || v.vrsta === 'PRIKOLICA';
    const partner = partnerVozila(v);

    /* ---- vozilo bez sprege: silueta pa tablica ispod nje ----
       Sva vozila se crtaju u ISTOJ razmeri (visina 22px), pa svako ima svoju
       sirinu siluete — duzina vozila se razlikuje. Kutija je svima 66px, koliko
       traži tablica ispod, a i najduza silueta (poluprikolica) staje u nju. */
    if (!partner) {
        const obrnut = naZapad ? ' gps-obrnut' : '';
        const oblik = v.vrsta === 'POLUPRIKOLICA' ? ' gps-oblik-poluprikolica'
                    : v.vrsta === 'PRIKOLICA'     ? ' gps-oblik-prikolica' : '';
        return L.divIcon({
            className: 'gps-oznaka-wrap',
            html: `<div class="${omot}${jaSamPrikolica ? ' gps-prikolica' : ''}${oblik}${klaseAlarma(uAlarmu, izabrano)}">
                     <span class="gps-kamion${obrnut}">${OBLIK[v.vrsta] || SVG_KAMION}</span>
                     <span class="gps-reg">${bezHtml(v.registracija)}</span>
                   </div>`,
            iconSize: [70, 46],
            iconAnchor: [35, 23],
        });
    }

    /* ---- sprega ----
       Sanduk poluprikolice NALEZE preko sasije tegljaca, kao na putu: kabina
       viri ispred, pogonski tockovi vire ispod sanduka. Zato siluete ne stoje
       jedna do druge nego se preklapaju — mere su u .gps-sprega-siluete.

       Sta je partner zavisi od strane: vuci je partner prikolica, prikolici
       vuca. Tablica po ULOZI ide na svoje mesto ispod svog vozila, a po tome
       KO NOSI UREDJAJ dobija izgled: sopstvena je krupnija, partnerova tisa. */
    const jaJe = {
        reg: v.registracija,
        klasa: 'gps-reg',
        alarm: klaseAlarma(uAlarmu, izabrano),
    };
    const on = {
        reg: partner,
        klasa: `gps-reg-par${v.nalog_aktivan ? '' : ' gps-reg-par-stari'}`,
        alarm: klaseAlarma(alarmPartnera, izabrano),
    };
    const prikolica = jaSamPrikolica ? jaJe : on;
    const vuca      = jaSamPrikolica ? on   : jaJe;

    const silueta = (uloga, deo, oblik) =>
        `<span class="gps-deo gps-deo-${uloga}${deo.alarm}">
           <span class="gps-kamion">${oblik}</span>
         </span>`;

    /* Sidro stoji pod vozilom koje NOSI UREDJAJ — njegova tacka na mapi ne sme
       da se pomeri kad se zakaci ili otkaci prikolica. Vrednosti su sredine
       silueta iz .gps-sprega-siluete (sanduk 14–89, tegljac 66–106). */
    const sredinaJa = jaSamPrikolica ? 51 : 86;

    return L.divIcon({
        className: 'gps-oznaka-wrap',
        html: `<div class="${omot} gps-sprega${naZapad ? ' gps-sprega-zapad' : ''}">
                 <span class="gps-sprega-siluete">
                   ${silueta('prikolica gps-prikolica', prikolica, SVG_POLUPRIKOLICA)}
                   ${silueta('vuca', vuca, SVG_VOZILO)}
                 </span>
                 <span class="gps-sprega-tablice">
                   <span class="${prikolica.klasa}${prikolica.alarm}">${bezHtml(prikolica.reg)}</span>
                   <span class="${vuca.klasa}${vuca.alarm}">${bezHtml(vuca.reg)}</span>
                 </span>
               </div>`,
        iconSize: [120, 48],
        iconAnchor: [naZapad ? 120 - sredinaJa : sredinaJa, 24],
    });
};

/* Koliko se sveta vidi oko izabranog vozila. Pedeset kilometara je oko
   pola sata voznje — dovoljno da se vidi sta vozilo ceka, a dovoljno blizu da
   se jos razaznaju pojedinacni putevi i nazivi mesta.
   `toBounds` prima STRANICU kvadrata, pa ide dvostruko. */
const OKO_VOZILA_M = 50000;

/* Pomeranje mape je odvojena komponenta jer se do instance mape dolazi samo
   preko useMap iznutra. Zumira se samo kad se izbor STVARNO promeni — inace bi
   osvezavanje na 30s vracalo pogled i otimalo mapu korisniku koji je odvukao.

   Uklapa se KRUG OKO VOZILA, a ne fiksan zum: na fiksnom zumu se oko vozila
   vidi uvek isti broj piksela, ali ne i isti broj kilometara — zavisi od
   geografske sirine. Ovako dispecer uvek dobije isti domet, bilo da je vozilo
   u Grckoj ili u Svedskoj. */
const PomeriNaVozilo = ({ vozilo, kljucSkoka }) => {
    const map = useMap();
    const zadnji = useRef(null);

    useEffect(() => {
        if (!vozilo || kljucSkoka === zadnji.current) return;
        zadnji.current = kljucSkoka;
        map.flyToBounds(
            L.latLng(vozilo.lat, vozilo.lng).toBounds(OKO_VOZILA_M * 2),
            { duration: 0.8, padding: [20, 20] }
        );
    }, [vozilo, kljucSkoka, map]);

    return null;
};

/* Jedno dugme u levoj koloni kontrola. Leaflet u uglu topleft slaze kontrole
   REDOM DODAVANJA, pa poredak u JSX-u odredjuje poredak na ekranu — ispod +/-
   idu: ceo ekran, podloga, saobracaj.

   Sadrzaj se pravi rucno (L.DomUtil), a ne React-om, jer Leaflet kontrola zivi
   van React stabla. Zato je i klik zaustavljen: bez `DomEvent.stop` propada na
   mapu i zumira je.

   ── ZASTO SE KONTROLA PRAVI TACNO JEDNOM ───────────────────────────────────
   „Redom dodavanja" znaci da je poredak posledica ISTORIJE, a ne opisa. Dok su
   `aktivno`, `iskljuceno`, `ikona` i `naslov` stajali u zavisnostima efekta,
   svaka njihova promena je rusila kontrolu i dodavala je iznova — a novo
   dodavanje ide na KRAJ kolone. Zato su dugmad menjala mesta cim se neko
   ukljuci, promeni podloga ili se prevede naslov.

   Zato je podeljeno na dvoje: kontrola se pravi jednom i ostaje na svom mestu,
   a sve sto se menja upisuje se u postojeci element. Isto vazi i za rukovalac
   i za `iskljuceno` u njemu — drze se u ref-u, jer bi inace ponovo usli u
   zavisnosti i vratili isti problem na mala vrata. */
const DugmeKontrole = ({ ikona, naslov, aktivno, iskljuceno, onKlik }) => {
    const map = useMap();
    const rukovalac = useRef(onKlik);
    rukovalac.current = onKlik;
    const iskljucenoRef = useRef(iskljuceno);
    iskljucenoRef.current = iskljuceno;
    const dugme = useRef(null);

    useEffect(() => {
        const Kontrola = L.Control.extend({
            options: { position: 'topleft' },
            onAdd() {
                const okvir = L.DomUtil.create('div', 'leaflet-bar gps-kontrola-mape');
                const a = L.DomUtil.create('a', '', okvir);
                a.href = '#';
                a.setAttribute('role', 'button');

                L.DomEvent.on(a, 'click', L.DomEvent.stop);
                L.DomEvent.on(a, 'click', () => { if (!iskljucenoRef.current) rukovalac.current?.(); });
                dugme.current = a;
                return okvir;
            },
        });

        const kontrola = new Kontrola();
        map.addControl(kontrola);
        return () => { map.removeControl(kontrola); dugme.current = null; };
    }, [map]);

    /* Izgled i stanje se UPISUJU u postojece dugme. Ovaj efekat je deklarisan
       posle onog koji pravi kontrolu, pa pri prvom crtanju zatice `dugme.current`
       vec popunjen. */
    useEffect(() => {
        const a = dugme.current;
        if (!a) return;
        a.innerHTML = ikona;
        a.title = naslov;
        a.setAttribute('aria-label', naslov);
        a.setAttribute('aria-pressed', aktivno ? 'true' : 'false');
        L.DomUtil[aktivno ? 'addClass' : 'removeClass'](a, 'gps-kontrola-aktivna');
        L.DomUtil[iskljuceno ? 'addClass' : 'removeClass'](a, 'gps-kontrola-iskljucena');
    }, [ikona, naslov, aktivno, iskljuceno]);

    return null;
};

/* „Otvori u Google Maps" — pin sa strelicom izlaza. Jednobojno, kao i ostale
   kontrole: Google-ove boje i logo su zasticen znak i ne smeju da se crtaju
   rucno, a i sarena ikona medju sivima bi vukla paznju koju ova radnja nema. */
const IKONA_GOOGLE_MAPS = `
<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
     stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="M11.6 21.2s-6.4-7-6.4-11.2a6.4 6.4 0 0 1 11.2-4.2"/>
  <circle cx="11.6" cy="10" r="2.3"/>
  <path d="M15.5 15.5h5.2v5.2M20.7 15.5l-6 6"/>
</svg>`;

/* Dva Leaflet sloja („pane") za trase plana: jedan za sporedne, jedan za
   izabranu.

   ── ZASTO NE REDOSLED CRTANJA ──────────────────────────────────────────────
   Ranije je ovde stajalo sortiranje koje je izabranu trasu stavljalo poslednju
   u nizu. To NE RADI, i to je bio pravi uzrok sto je izabrana nestajala ispod
   sivih: React preslaguje decu po `key`, ali ih pri tome ne montira iznova —
   samo ih premesta u svom stablu. Leaflet sloj je u pane vec ubacen pri
   montiranju i tu ostaje, pa promena redosleda u JSX-u ne menja nista na mapi.
   Izgledalo je kao da je popravljeno jer pri PRVOM crtanju redosled montiranja
   jeste bio ispravan.

   Paneovi resavaju to na nivou na kom problem i postoji: sadrzaj sloja sa vecim
   `z-index` je iznad, bez obzira kojim su redom pojedinacne linije ubacene.

   Brojevi: Leaflet drzi `overlayPane` na 400, a `markerPane` na 600. 410 i 420
   staju izmedju — iznad predjenog puta i oznaka stajanja, ispod oblacica i
   vozila. */
const SLOJ_SPOREDNE = 'gps-plan-sporedne';
const SLOJ_IZABRANA = 'gps-plan-izabrana';

const SlojeviPlana = () => {
    const map = useMap();

    useEffect(() => {
        [[SLOJ_SPOREDNE, 410], [SLOJ_IZABRANA, 420]].forEach(([ime, z]) => {
            const sloj = map.getPane(ime) || map.createPane(ime);
            sloj.style.zIndex = String(z);
        });
    }, [map]);

    return null;
};

/* Javlja koji se deo sveta trenutno vidi.

   Sluzi odsecanju oznaka radova: cela flota vraca preko hiljadu incidenata, a
   na ekranu ih staje nekoliko desetina. Leaflet ne odseca sam — svaki marker je
   element u DOM-u koji se pri svakom zumiranju premesta, bez obzira da li se
   uopste vidi.

   Prati se `moveend` i `zoomend`, ne `move` — taj se okida na svaki pomeraj
   misa i pretvorio bi ustedu u trosak. Jednom se javlja i odmah po montiranju,
   jer do prvog pomeranja mape moze proci dosta vremena. */
const PratiOkvir = ({ onOkvir }) => {
    const map = useMap();

    useEffect(() => {
        onOkvir(map.getBounds());
    }, [map, onOkvir]);

    useMapEvents({
        moveend: (e) => onOkvir(e.target.getBounds()),
        zoomend: (e) => onOkvir(e.target.getBounds()),
    });

    return null;
};

/* Ranije je ovde stajalo dugme „otvori trenutni prikaz". Ta radnja je ostala
   — ali u izborniku podloga, gde joj je i mesto (ne trazi nikakav preduslov).
   Dugme u koloni je zato preuzelo ono sto se stvarno radi sa izabranim
   vozilom: posalji vozacu gde je, ili kuda ide. */

const DugmeCeoEkran = ({ ceoEkran, onPrebaci, naslov }) => (
    <DugmeKontrole
        ikona={`<i class="pi ${ceoEkran ? 'pi-window-minimize' : 'pi-window-maximize'}"></i>`}
        naslov={naslov}
        onKlik={onPrebaci}
    />
);

/* Posle prelaska u ceo ekran (i nazad) Leaflet ne zna da mu se platno promenilo
   — bez ovoga ostaju sive praznine umesto plocica. */
const OsveziVelicinu = ({ okidac }) => {
    const map = useMap();
    useEffect(() => {
        const t = setTimeout(() => map.invalidateSize(), 120);
        return () => clearTimeout(t);
    }, [okidac, map]);
    return null;
};


/* Uklapanje SVIH predlozenih trasa u vidno polje.

   Bez ovoga se trase iscrtaju, ali se ne vide: posle izbora vozila mapa je
   zumirana na 50 km oko njega, a trasa ide stotinama kilometara dalje. Izgleda
   kao da nista nije nacrtano.

   Uklapaju se SVE, ne samo izabrana — poenta predloga je poredjenje, a porediti
   se moze samo ono sto se vidi. Kljuc je broj trasa i odrediste, pa se pogled
   namesta kad stigne NOV racun, a ne pri svakoj promeni izbora — inace bi mapa
   skakala svaki put kad se klikne druga kartica. */
const UklopiTrase = ({ rute, kljuc }) => {
    const map = useMap();
    const zadnji = useRef(null);

    useEffect(() => {
        if (!rute || !rute.length || kljuc === zadnji.current) return;
        const tacke = rute.flatMap(r => r.geometrija || []);
        if (tacke.length < 2) return;
        zadnji.current = kljuc;
        map.fitBounds(L.latLngBounds(tacke), { padding: [50, 50] });
    }, [rute, kljuc, map]);

    return null;
};

/* ---- pretraga i lokacije na mapi (10.09.2026) ---- */

/* Pin pretrage je KAP, ne krug — mora da se razlikuje od vozila na prvi
   pogled, jer je jedini objekat na mapi koji nije vozilo. Vrh kapi je tacka
   koju pin oznacava, pa sidro ide na dno, ne u sredinu. */
const IKONA_PIN = L.divIcon({
    className: 'gps-pin-ikona',
    html: `<svg viewBox="0 0 24 34" width="30" height="42" aria-hidden="true">
             <path d="M12 0.9C6.2 0.9 1.5 5.6 1.5 11.4c0 7.6 9 20.2 9.4 20.8a1.4 1.4 0 0 0 2.2 0c.4-.6 9.4-13.2 9.4-20.8C22.5 5.6 17.8.9 12 .9z"/>
             <circle cx="12" cy="11.4" r="4.1"/>
           </svg>`,
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -38],
});

/* Sacuvana lokacija je manja i mirnija od pina pretrage: nju operater nije
   trazio, ona samo stoji na mapi. Da je iste velicine, deset sacuvanih mesta
   bi zagusilo pogled na vozila, zbog kojih se mapa i gleda. */
const IKONA_LOKACIJA = L.divIcon({
    className: 'gps-lok-ikona',
    html: `<span class="gps-lok-tacka"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -10],
});

/* Tacka merenja nosi svoj redni broj — bez njega se na mapi ne vidi kojim
   redom su tacke spojene, a ruta kroz tri tacke nije ista u oba smera. */
const ikonaMerenja = (redni, prva, poslednja) => L.divIcon({
    className: 'gps-mer-ikona',
    html: `<span class="gps-mer-tacka${prva ? ' gps-mer-tacka-prva' : ''}${poslednja ? ' gps-mer-tacka-zadnja' : ''}">${redni}</span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
});

/* Klik na mapu dodaje tacku merenja — ali SAMO dok merenje traje. Van merenja
   mapa mora da ostane obicna mapa: klik koji uvek nesto dodaje pretvara
   pomeranje pogleda u niz slucajnih tacaka. */
const KlikNaMapu = ({ aktivno, onKlik }) => {
    useMapEvents({
        click: (e) => { if (aktivno) onKlik(e.latlng.lat, e.latlng.lng); },
    });
    return null;
};

/* Skok na izabranu tacku. Zum se NE menja kad je korisnik vec blizu — naglo
   priblizavanje na mestu gde se vec gleda gubi kontekst koji je operater sam
   namestio. */
const PomeriNaTacku = ({ tacka, kljucSkoka }) => {
    const mapa = useMap();
    useEffect(() => {
        if (!tacka) return;
        const zum = Math.max(mapa.getZoom(), 13);
        mapa.flyTo([tacka.lat, tacka.lng], zum, { duration: 0.6 });
    }, [tacka, kljucSkoka, mapa]);
    return null;
};

/* Naziv tacke za spisak merenja. Adresa sa mape ume da bude ceo pasus, a u
   uskom panelu se cita samo pocetak — zato prvi deo, pa koordinate kao rezerva
   kad naziva uopste nema (klik na praznu mapu). */
const nazivTacke = (t) =>
    t.naziv || `${Number(t.lat).toFixed(4)}, ${Number(t.lng).toFixed(4)}`;

const GPS = () => {
    const { t, i18n } = useTranslation();
    const auth = useContext(AuthContext);
    const history = useHistory();
    const { can } = usePermission();
    const toast = useRef(null);

    const [vozila, setVozila]         = useState([]);
    const [tragovi, setTragovi]       = useState([]);
    const [ruta, setRuta]             = useState(null);
    const [stajanja, setStajanja]     = useState([]);
    /* Deonica na koju je KLIKNUTO. Deonica pod misem se namerno NE drzi u
       stanju — vidi `naMisPoRuti`. */
    const [segmentKlik, setSegmentKlik]   = useState(null);
    const [izabrano, setIzabrano]     = useState(null);   // mobilisis_id
    const [prikaziTrag, setPrikaziTrag] = useState(true);
    const [dijalogAlarma, setDijalogAlarma]     = useState(false);  // prekidač „A" otvara izbor
    // Alarmi vozila rade od ulaska na stranicu — dispečer ne treba da ih pali svaki put.
    const [izborAlarma, setIzborAlarma]         = useState({ vozila: true, vozaci: false, fakture: false });
    /* Sva tri stanja su upaljena od početka i to se NE pamti — svako otvaranje
       mape kreće od pune slike.

       Ranije je prazan spisak značio „vidi se sve", a klik na čip sužavao izbor.
       To je čitljivo tek kad se zna pravilo: čip bez obruba je značio i „ova
       vozila se vide" (jer se vide sva) i „ovo nije izabrano" — dva različita
       stanja istog izgleda. Prekidač vidljivosti nema tu dvosmislenost: upaljen
       znači da se vidi, ugašen da se ne vidi, uvek. */
    const [vidljivaStanja, setVidljivaStanja] = useState(() => STANJA_VOZILA.map(x => x.kljuc));
    const [aktivnaVrsta, setAktivnaVrsta]       = useState(null);   // vrsta koja je trenutno na ekranu
    const [sakrijPrikaz, setSakrijPrikaz]       = useState(false);  // X sklanja samo tekući prikaz
    const [alarmi, setAlarmi]                   = useState([]);
    const [ucitava, setUcitava]       = useState(false);
    const [poslednje, setPoslednje]   = useState(null);
    const [kljucSkoka, setKljucSkoka] = useState(0);
    const [detalji, setDetalji]       = useState(null);
    const [ucitavaDetalje, setUcitavaDetalje] = useState(false);
    const [ceoEkran, setCeoEkran]     = useState(false);

    const okvirMape = useRef(null);
    /* Izbornik podloga stoji IZVAN MapContainer-a (lebdi nad mapom), pa nema
       `useMap()`. Za stavku „otvori trenutni prikaz" mu treba centar i zum,
       zato se instanca mape pamti preko ref-a. */
    const mapaRef = useRef(null);

    /* Ceo ekran ide preko Fullscreen API-ja kad ga pregledac daje — tada nestaje
       i traka pregledaca, sto je i smisao. Ako ga nema (ili odbije), pada se na
       CSS koji mapu razvuce preko celog prozora. */
    const prebaciCeoEkran = useCallback(() => {
        const el = okvirMape.current;
        if (!el) return;

        if (document.fullscreenElement) {
            document.exitFullscreen?.();
            return;
        }
        if (el.requestFullscreen) {
            el.requestFullscreen().catch(() => setCeoEkran(p => !p));
        } else {
            setCeoEkran(p => !p);
        }
    }, []);

    /* Stanje se cita iz pregledaca, ne pamti se lokalno — korisnik iz celog
       ekrana moze izaci i tasterom Esc, mimo naseg dugmeta. */
    useEffect(() => {
        const naPromenu = () => setCeoEkran(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', naPromenu);
        return () => document.removeEventListener('fullscreenchange', naPromenu);
    }, []);

    /* Zahtevi se prekidaju pri odlasku sa strane — bez ovoga bi tajmer od 30s
       nastavio da radi setState nad otkacenom komponentom. */
    const ziv = useRef(true);
    useEffect(() => () => { ziv.current = false; }, []);

    const zovi = useCallback(async (putanja, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + putanja, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + auth.token,
            },
            body: JSON.stringify(telo),
        });
        if (!r.ok) {
            const greska = await r.json().catch(() => ({}));
            throw new Error(greska.message || t('gps.greskaUcitavanja'));
        }
        return r.json();
    }, [auth.token, t]);

    /* Jedno osvezavanje: pozicije + tragovi. Tragovi se ne povlace kad je
       iskljucen prikaz ili kad je izabrano jedno vozilo (tada se crta cela ruta). */
    const osveziPozicije = useCallback(async (tiho = false) => {
        if (!tiho) setUcitava(true);
        try {
            const zivo = await zovi('/gps/zivo');
            if (!ziv.current) return;
            setVozila(Array.isArray(zivo) ? zivo : []);
            setPoslednje(new Date());

            if (prikaziTrag && !izabrano) {
                const tacke = await zovi('/gps/rute', { minuta: TRAG_MINUTA });
                if (!ziv.current) return;
                setTragovi(Array.isArray(tacke) ? tacke : []);
            } else {
                setTragovi([]);
            }
        } catch (e) {
            if (!ziv.current) return;
            // Tiho osvezavanje ne sme da zatrpa ekran toastovima na svakih 30s.
            if (!tiho) {
                toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
            }
        } finally {
            if (ziv.current && !tiho) setUcitava(false);
        }
    }, [zovi, prikaziTrag, izabrano, t]);

    /* Cela ruta izabranog vozila — period drzi RUTA_SATI.

       Stajanja se traze ISTIM pozivom, ali odvojenim zahtevom: racunaju se u
       bazi nad istim tackama, a stizu nezavisno da sporiji od njih dvoje ne
       zadrzava drugi. Ako stajanja zakazu, ruta se svejedno crta. */
    const ucitajRutu = useCallback(async (mobilisis_id) => {
        zovi('/gps/stajanja', { mobilisis_id, sati: RUTA_SATI })
            .then(d => { if (ziv.current) setStajanja(Array.isArray(d) ? d : []); })
            .catch(() => { if (ziv.current) setStajanja([]); });

        try {
            const r = await zovi('/gps/ruta', { mobilisis_id, sati: RUTA_SATI });
            if (!ziv.current) return;
            setRuta(r && Array.isArray(r.tacke) ? r : { tacke: [], sazetak: {} });
        } catch (e) {
            if (ziv.current) {
                toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
            }
        }
    }, [zovi, t]);

    // Prvo punjenje + tajmer. Tajmer radi i dok se gleda mapa — to je i trazeno.
    useEffect(() => {
        osveziPozicije(false);
        const tajmer = setInterval(() => osveziPozicije(true), OSVEZAVANJE_MS);
        return () => clearInterval(tajmer);
    }, [osveziPozicije]);

    /* Detalji za bocni panel. Odvojeni od rute jer stizu iz drugog izvora i
       sporije su (Mobilisis), pa panel ne sme da ceka rutu ni obrnuto. */
    const ucitajAlarme = useCallback(async () => {
        try {
            const d = await zovi('/gps/alarmi');
            if (!ziv.current) return;
            setAlarmi(Array.isArray(d) ? d : []);
        } catch (e) {
            // Alarmi su dodatak mapi — neuspeh ne sme da zatrpa ekran toastom.
            if (ziv.current) console.warn('Alarmi vozila nisu učitani:', e.message);
        }
    }, [zovi]);

    /* Koje su vrste uključene — kao string, da efekat ispod ne kreće iznova pri
       svakom renderu: niz bi svaki put bio nov objekat i tajmer bi se resetovao. */
    const ukljuceneVrste = useMemo(
        () => VRSTE_ALARMA.filter(v => v.dostupno && izborAlarma[v.kljuc]).map(v => v.kljuc).join(','),
        [izborAlarma]
    );

    /* Ciklus prikaza. Jedan tajmer koji sam sebe prezakazuje — smenjuje fazu i
       bira sledeće trajanje prema njoj, umesto dva tajmera koja bi se s vremenom
       razišla. Gašenjem svih prekidača sve se čisti.

       Vrste dolaze na red jedna po jedna, nikad dve odjednom. */
    useEffect(() => {
        if (!ukljuceneVrste) {
            setAktivnaVrsta(null);
            return;
        }

        const vrste = ukljuceneVrste.split(',');
        let tajmer;
        let prikaz = true;
        let red = 0;

        const korak = () => {
            if (!ziv.current) return;
            if (prikaz) {
                setAktivnaVrsta(vrste[red % vrste.length]);
                // Sakrivanje važi samo za onaj prikaz koji je korisnik sklonio.
                setSakrijPrikaz(false);
            } else {
                setAktivnaVrsta(null);
                red += 1;   // sledeći prikaz pripada sledećoj vrsti
            }
            tajmer = setTimeout(() => {
                prikaz = !prikaz;
                korak();
            }, prikaz ? ALARM_PRIKAZ_MS : ALARM_PAUZA_MS);
        };

        korak();
        return () => clearTimeout(tajmer);
    }, [ukljuceneVrste]);

    /* Alarmi se drze ucitani UVEK, nezavisno od prekidaca i od toga da li je
       spisak na ekranu: sluze i oznakama na mapi i panelu izabranog vozila.
       Prekidac odlucuje samo sta se od toga PRIKAZUJE. */
    useEffect(() => {
        ucitajAlarme();
        const tajmer = setInterval(ucitajAlarme, ALARM_OSVEZAVANJE_MS);
        return () => clearInterval(tajmer);
    }, [ucitajAlarme]);

    const ucitajDetalje = useCallback(async (mobilisis_id) => {
        setDetalji(null);
        setUcitavaDetalje(true);
        try {
            const d = await zovi('/gps/detalji', { mobilisis_id });
            if (!ziv.current) return;
            setDetalji(d);
        } catch (e) {
            if (ziv.current) {
                toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
            }
        } finally {
            if (ziv.current) setUcitavaDetalje(false);
        }
    }, [zovi, t]);

    // Izbor vozila povlaci njegovu rutu i detalje; poništavanje izbora ih briše.
    useEffect(() => {
        if (izabrano) {
            ucitajRutu(izabrano);
            ucitajDetalje(izabrano);
        } else {
            setRuta(null);
            setDetalji(null);
        }
    }, [izabrano, ucitajRutu, ucitajDetalje]);

    const voziloIzabrano = useMemo(
        () => vozila.find(v => v.mobilisis_id === izabrano) || null,
        [vozila, izabrano]
    );

    /* Kad je vozilo izabrano, na mapi ostaje samo ono — trazeno je da se vidi
       "samo njega zumiranog". Inace vazi filter sa cipova: prazan filter znaci
       sva vozila, a ne nijedno — cipovi su suzavanje, ne obavezan izbor. */
    const zaPrikaz = useMemo(() => {
        if (izabrano) return vozila.filter(v => v.mobilisis_id === izabrano);
        return vozila.filter(v => vidljivaStanja.includes(stanjeVozila(v)));
    }, [vozila, izabrano, vidljivaStanja]);

    /* Tragovi prate isti filter — inace bi ostajale plave linije vozila
       koja se vise ne vide. */
    const vidljivaVozila = useMemo(
        () => new Set(zaPrikaz.map(v => v.mobilisis_id)),
        [zaPrikaz]
    );


    /* Ravan niz tacaka se grupise u polilinije po vozilu — SP vraca sortirano
       po (vozilo, vreme), pa je dovoljno jedno prolazenje. */
    const linijeTragova = useMemo(() => {
        const po = new Map();
        for (const t2 of tragovi) {
            if (!po.has(t2.mobilisis_id)) po.set(t2.mobilisis_id, []);
            po.get(t2.mobilisis_id).push([t2.lat, t2.lng]);
        }
        return [...po.entries()].filter(([id, tacke]) => tacke.length > 1 && vidljivaVozila.has(id));
    }, [tragovi, vidljivaVozila]);

    /* Ruta se NE crta kao jedna linija. Kad pracenje stane (tunel, ugasen uredjaj,
       pad servera), poslednja tacka pre rupe i prva posle nje su daleko jedna od
       druge — jedna polilinija bi ih spojila pravom crtom preko pola Evrope, kao
       da je vozilo tuda proslo. Zato se seca na segmente, a sama rupa se crta
       isprekidano i sivo: vidi se da veza postoji, ali da putanja nije poznata. */
    const { segmenti, rupe } = useMemo(() => {
        const tacke = ruta?.tacke || [];
        const seg = [];
        const rup = [];
        let tekuci = [];

        tacke.forEach((p, i) => {
            if (p.prekid && tekuci.length) {
                seg.push(tekuci);
                const prethodna = tacke[i - 1];
                rup.push([[prethodna.lat, prethodna.lng], [p.lat, p.lng]]);
                tekuci = [];
            }
            tekuci.push([p.lat, p.lng]);
        });
        if (tekuci.length) seg.push(tekuci);

        // Segment od jedne tacke nije linija — Leaflet bi ga svejedno primio, ali se ne vidi.
        return { segmenti: seg.filter(s => s.length > 1), rupe: rup };
    }, [ruta]);

    /* Koja vozila su trenutno alarmirana. Set, ne pretraga po nizu za svaki
       marker — flota ume da ima stotinu vozila, a mapa se crta na svakih 30s. */
    const alarmiranaVozila = useMemo(() => {
        if (!izborAlarma.vozila) return new Map();
        const m = new Map();
        alarmi.forEach(a => {
            if (!a.mobilisis_id || a.dana_do > ALARM_PRAG_DANA) return;
            const nivo = a.dana_do < 0 ? 'isteklo' : 'uskoro';
            // Vozilo sa vise alarma nosi onaj tezi — isteklo nadjacava "uskoro".
            if (nivo === 'isteklo' || !m.has(a.mobilisis_id)) m.set(a.mobilisis_id, nivo);
        });
        return m;
    }, [alarmi, izborAlarma.vozila]);

    /* Isti alarmi, ali po tablici. Prikolica po pravilu nije na GPS-u pa nema
       mobilisis_id — a njeni dokumenti isticu kao i svima. Bez ovoga bi
       prikljucno vozilo u sprezi ostalo neoznaceno iako mu je nesto isteklo.
       Registracija se poredi ogoljena (golaReg), jer ista tablica ume da stoji
       kao "AB-649VS" u GPS zapisu i "AB-649 VS" u voznom parku. */
    const alarmiPoTablici = useMemo(() => {
        if (!izborAlarma.vozila) return new Map();
        const m = new Map();
        alarmi.forEach(a => {
            if (!a.registracija || a.dana_do > ALARM_PRAG_DANA) return;
            const k = golaReg(a.registracija);
            const nivo = a.dana_do < 0 ? 'isteklo' : 'uskoro';
            if (nivo === 'isteklo' || !m.has(k)) m.set(k, nivo);
        });
        return m;
    }, [alarmi, izborAlarma.vozila]);

    /* Alarmi bas izabranog vozila — panel ih pokazuje bez obzira na to da li
       alarmi kruze po mapi. */
    const alarmiIzabranog = useMemo(
        () => (izabrano ? alarmi.filter(a => a.mobilisis_id === izabrano) : []),
        [alarmi, izabrano]
    );

    /* Spisak se otvara samo kad ZAISTA ima sta da se pokaze. Prazan prozor sa
       porukom "nema alarma" bi svakih pet minuta prekrivao mapu da bi rekao da
       je sve u redu — a to se vidi i po tome sto se nije pojavio. */
    const prikazAlarma = !izabrano && aktivnaVrsta === 'vozila'
        && !sakrijPrikaz && alarmi.length > 0;

    const brojac = useMemo(() => {
        const c = { vozi: 0, stoji: 0, staro: 0 };
        vozila.forEach(v => { c[stanjeVozila(v)] += 1; });
        return c;
    }, [vozila]);

    /* Klik pali ili gasi tu kategoriju. Gasenje SVE TRI je dozvoljeno i daje
       praznu mapu — to nije greska nego ono sto je trazeno, a tri ugasena cipa
       stoje tu i objasnjavaju zasto je prazna. Zabrana bi cuvala korisnika od
       radnje koju je svesno napravio i koju jednim klikom ponistava. */
    const prebaciStanje = (kljuc) => {
        setVidljivaStanja(v => v.includes(kljuc) ? v.filter(x => x !== kljuc) : [...v, kljuc]);
    };

    const izaberi = (id) => {
        setIzabrano(id || null);
        /* Stajanja pripadaju PRETHODNOM vozilu — bez ovoga bi na trenutak
           stajala nad rutom novog, dok ne stignu njegova. Isto vazi i za
           otvorenu karticu deonice. */
        setStajanja([]);
        setSegmentKlik(null);
        /* Plan pripada PRETHODNOM vozilu — trase bi inace ostale na mapi kao
           da vaze za novo. */
        setPlanOtvoren(false);
        setPlanRute([]);
        setPlanIzabrana(null);
        setKljucSkoka(k => k + 1);   // isti izbor ponovo = ponovni skok na vozilo
    };

    /* ═══════════════════════════════════════════════════════════════════════
       GLAVNA PRETRAGA — pin, sacuvane lokacije, blizina, merenje  (10.09.2026)

       Sve sto sledi iz jednog polja u zaglavlju. Drzi se ovde, u GPS-u, a ne u
       samoj pretrazi, jer se svaka radnja zavrsava NA MAPI: pin, linija,
       skok pogleda. Pretraga zna sta je nadjeno, mapa zna sta sa tim.
       ═══════════════════════════════════════════════════════════════════════ */
    const izaberiRef = useRef(null);
    izaberiRef.current = izaberi;

    const [podesavanja, setPodesavanja]           = useState(PODRAZUMEVANO);
    const [tipoviLokacije, setTipoviLokacije]     = useState([]);
    const [dijalogPodesavanja, setDijalogPodesavanja] = useState(false);
    const [cuvaPodesavanja, setCuvaPodesavanja]   = useState(false);

    const [pin, setPin]               = useState(null);   // tacka koju gledamo
    const [kljucPina, setKljucPina]   = useState(0);      // isti pin ponovo = ponovni skok
    const [lokacije, setLokacije]     = useState([]);     // sacuvana mesta firme
    const [dijalogLokacija, setDijalogLokacija] = useState(false);
    const [cuvaLokaciju, setCuvaLokaciju]       = useState(false);

    /* Izgled mape: koja podloga i da li se crta saobracaj. Licna postavka, kao
       i pretraga — dispecer koji radi na satelitu ne zeli da mu se mapa vraca
       na linijsku pri svakom otvaranju. */
    const [mapa, setMapa] = useState({ sloj: SLOJEVI[0].kljuc, saobracaj: false });
    const [izbornikSlojeva, setIzbornikSlojeva] = useState(false);
    const [izbornikGoogle, setIzbornikGoogle]   = useState(false);

    /* Šablon za pločice gustine stiže sa servera, zajedno sa ostalim
       podešavanjima. Ključ tako ostaje samo u backend `.env` — ne u frontend
       fajlu, ne u build-u, ne u repozitorijumu. */
    const [saobracajKonfig, setSaobracajKonfig] = useState(null);

    /* Radovi, blokade i nezgode. Odvojeno od `mapa.saobracaj` (gustina) jer je
       to drugi podatak: gustina je slika koju povlaci pregledac, incidenti su
       dogadjaji koje dovlaci backend i nad kojima se moze kliknuti.

       Vezani su za IZABRANO VOZILO, ne za vidno polje mape: dispecerovo pitanje
       nikad nije „sta ima u Evropi" nego „sta ceka OVO vozilo". Uz to, vidno
       polje se menja pri svakom pomeranju mape, pa bi se placalo i ono sto niko
       nije trazio. */
    const [incidenti, setIncidenti]         = useState([]);
    const [prikaziIncidente, setPrikaziIncidente] = useState(false);

    /* Tačke na mapi (pumpe, klijenti, carine…) i sedišta firmi. Sloj je
       ugašen dok se ne traži — podatak je mali, ali mapa se otvara i kad
       nikoga ne zanimaju pinovi. */
    const [prikaziTacke, setPrikaziTacke] = useState(false);
    const [tacke, setTacke] = useState([]);
    const [sedistaFirmi, setSedistaFirmi] = useState([]);
    /* Ugašeni tipovi, ne upaljeni: nov tip u šifarniku se tako pojavi sam,
       bez diranja koda. */
    const [ugaseniTipovi, setUgaseniTipovi] = useState(() => new Set());
    const [sedistaUgasena, setSedistaUgasena] = useState(false);

    /* Učitava se jednom, pri prvom paljenju sloja: tačke se menjaju u
       Administraciji, ne dok neko gleda mapu. Gašenje sloja ne baca podatak,
       pa ponovno paljenje ne zove server. */
    const [tackeUcitane, setTackeUcitane] = useState(false);
    useEffect(() => {
        if (!prikaziTacke || tackeUcitane) return;
        let otkazano = false;
        zovi('/tacke/zaMapu', {})
            .then(d => {
                if (otkazano) return;
                setTacke(d?.Tacke || []);
                setSedistaFirmi(d?.Sedista || []);
                setTackeUcitane(true);
            })
            .catch(() => {
                if (otkazano) return;
                /* Sloj se gasi sam: upaljeno dugme bez ijednog pina izgleda kao
                   da tačaka nema, a greška bi ostala neispričana. */
                setPrikaziTacke(false);
                toast.current?.show({ severity: 'warn', summary: t('tacke.greskaUcitavanja'), life: 4000 });
            });
        return () => { otkazano = true; };
    }, [prikaziTacke, tackeUcitane, zovi, t]);

    /* Tipovi koji stvarno postoje među učitanim tačkama — prekidači se prave
       po podacima, ne po šifarniku: tip bez ijedne tačke nema šta da gasi. */
    const tipoviNaMapi = useMemo(() => {
        const po = new Map();
        tacke.forEach(x => {
            const k = kljucTipa(x.tipOpis);
            if (!po.has(k)) po.set(k, { kljuc: k, naziv: x.tipOpis, broj: 0 });
            po.get(k).broj += 1;
        });
        return [...po.values()].sort((a, b) => a.naziv.localeCompare(b.naziv, 'sr'));
    }, [tacke]);

    const vidljiviTipovi = useMemo(
        () => new Set(tipoviNaMapi.map(x => x.kljuc).filter(k => !ugaseniTipovi.has(k))),
        [tipoviNaMapi, ugaseniTipovi]);

    const prebaciTip = (kljuc) => setUgaseniTipovi(prev => {
        const sledeci = new Set(prev);
        if (sledeci.has(kljuc)) sledeci.delete(kljuc); else sledeci.add(kljuc);
        return sledeci;
    });
    /* Front ne zna unapred da li backend ima kljuc provajdera — sazna tek iz
       prvog odgovora. Zato se dugme gasi naknadno, umesto da stoji ugaseno i
       kad sve radi. */
    const [incNedostupni, setIncNedostupni] = useState(false);

    /* Planiranje rute — gde vozilo TEK IDE. Odvojeno od rute na mapi, koja je
       istorija (gde je bilo). Predlozi se crtaju preko mape dok je prozor
       otvoren; izabrani je istaknut, ostali su tanji i bledji. */
    const [planOtvoren, setPlanOtvoren] = useState(false);
    const [planRute, setPlanRute]       = useState([]);
    const [planIzabrana, setPlanIzabrana] = useState(null);
    /* Raste na svaki NOV racun trasa. Po njemu mapa zna kad da namesti pogled,
       a kad da ga ostavi na miru (promena izabrane kartice nije nov racun). */
    const [planKljuc, setPlanKljuc]     = useState(0);
    /* Cilj se drzi OVDE, a ne samo u prozoru: prozor se sklanja u traku, a
       zastavica na mapi mora da ostane. */
    const [planCilj, setPlanCilj]       = useState(null);
    /* Otvoren incident — jedan po jedan, kao kod deonice rute. Brojač je tu
       jer se isti incident može kliknuti dvaput zaredom; bez njega bi ključ
       ostao isti, marker se ne bi ponovo montirao i popup se ne bi otvorio. */
    const [incidentKlik, setIncidentKlik] = useState(null);
    const [vidnoPolje, setVidnoPolje]     = useState(null);
    const brojacIncKlika = useRef(0);

    const [blizina, setBlizina] = useState(null);   // { lokacija, vozila, ucitava, rute, vazdusna }
    const [merenje, setMerenje] = useState(null);   // { tacke, rezultat, ucitava, poruka }

    /* Podesavanja i sifarnik tipova — jednom, pri otvaranju mape. Neuspeh se ne
       prijavljuje: pretraga radi i sa podrazumevanim postavkama, pa bi toast
       samo smetao na ekranu koji se ionako otvara. */
    useEffect(() => {
        (async () => {
            try {
                const d = await zovi('/gps/pretragaInit');
                if (!ziv.current) return;
                setPodesavanja(raspakuj((d?.PodesavanjaDB || [])[0]?.vrednost));
                setMapa(raspakujMapu((d?.MapaDB || [])[0]?.vrednost));
                setSaobracajKonfig(d?.saobracaj || null);
                setTipoviLokacije(d?.TipLokacijeDB || []);
            } catch (e) { /* podrazumevano je ispravno stanje */ }
        })();
    }, [zovi]);

    const ucitajLokacije = useCallback(async () => {
        try {
            const d = await zovi('/gps/lokacije');
            if (ziv.current) setLokacije(Array.isArray(d) ? d : []);
        } catch (e) { /* mapa radi i bez sacuvanih mesta */ }
    }, [zovi]);

    useEffect(() => { ucitajLokacije(); }, [ucitajLokacije]);

    const sacuvajPodesavanja = useCallback(async (izbor) => {
        setCuvaPodesavanja(true);
        try {
            const o = await zovi('/gps/podesavanja', { vrednost: upakuj(izbor) });
            if (!ziv.current) return;
            setPodesavanja(izbor);
            setDijalogPodesavanja(false);
            toast.current?.show({ severity: 'success', summary: t('common.sacuvano'), detail: o?.[0]?.message || t('gps.pretragaSacuvano'), life: 2500 });
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
        } finally {
            if (ziv.current) setCuvaPodesavanja(false);
        }
    }, [zovi, t]);

    /* Prekidaci podloge i saobracaja se snimaju ODMAH, bez dugmeta za potvrdu:
       to je izgled, a ne podatak — potvrda za promenu koju korisnik vec vidi
       na ekranu bila bi suvisan korak. Neuspeh se preslusa u tisini: mapa je
       vec prebacena, a postavka je sitnica koja se sledeci put opet klikne. */
    const promeniMapu = useCallback((novo) => {
        setMapa(novo);
        zovi('/gps/podesavanja', { kljuc: 'GPS_MAPA', vrednost: upakujMapu(novo) })
            .catch(() => {});
    }, [zovi]);

    /* Strelice se racunaju iz VEC isecenih segmenata, ne iz sirovih tacaka —
       tako nijedna ne padne preko rupe u pracenju i ne pokaze smer kroz
       deonicu kojom vozilo mozda nije ni proslo. */
    const strelice = useMemo(() => (izabrano ? tackeStrelica(segmenti) : []), [izabrano, segmenti]);

    /* Prelazak misem po ruti.

       Sadrzaj oblacica se upisuje DIREKTNO u Leaflet, bez React stanja. Dva
       razloga, oba su se videla u radu:

       1. Oblacic vezan tek kad stanje stigne NE OTVARA SE — Leaflet ga otvara
          na `mouseover`, a taj je vec prosao dok je React ponovo iscrtao.
          Zato se vezuje unapred (prazan), a ovde mu se samo menja sadrzaj.
       2. `mousemove` okida desetine puta u sekundi; svako postavljanje stanja
          bi ponovo iscrtalo celu mapu sa svim oznakama vozila i stajanja.

       Najbliza deonica se trazi linearnim prolazom — i ruta od 48h ima svega
       nekoliko hiljada tacaka, sto je za jedan prolaz zanemarljivo, dok bi se
       svaka slozenija struktura morala graditi iznova pri svakom osvezavanju. */
    const naMisPoRuti = useCallback((e) => {
        const sloj = e.target;
        if (!sloj || !sloj.setTooltipContent) return;

        const tacke = ruta?.tacke;
        if (!tacke || tacke.length < 2) return;

        const o = opisSegmenta(tacke, nadjiSegment(tacke, e.latlng));
        if (!o) return;

        sloj.setTooltipContent(
            `<div class="gps-oblacic gps-seg-oblacic">`
            + `<strong>${bezHtml(o.a.vreme)} · ~${o.prosek} km/h</strong>`
            + `<div class="gps-seg-oblacic-put">${bezHtml(duzinaTeksta(o.metara))}</div>`
            + `</div>`
        );
        /* Bez poziva „kliknite za detalje": oblacic prati mis i vidi se stalno,
           pa bi uputstvo u njemu bilo treci red koji se cita svaki put, a treba
           samo prvi put. Podatak je ono zbog cega se prelazi misem. */
    }, [ruta]);

    /* Brojac otvaranja. Kljuc popupa MORA da se promeni na svaki klik, inace
       se marker ne remontira i `openPopup()` se ne pozove — a indeks segmenta
       to ne garantuje: dva klika na razlicita mesta iste deonice daju isti
       indeks, pa se drugi klik ne bi video. To je bio uzrok „ne otvara se
       uvek". */
    const brojacKlika = useRef(0);

    const naKlikRute = useCallback((e) => {
        /* Bez ovoga klik propada na mapu, a mapa pri kliku zatvara otvoren
           popup (`closePopupOnClick`) — i to bas onaj koji smo upravo otvorili.
           Drugi uzrok istog kvara. */
        L.DomEvent.stopPropagation(e);

        const t = ruta?.tacke;
        if (!t || t.length < 2) return;
        const i = nadjiSegment(t, e.latlng);
        if (i < 0) return;

        brojacKlika.current += 1;
        setSegmentKlik({
            ...opisSegmenta(t, i),
            gde: [e.latlng.lat, e.latlng.lng],
            kljuc: brojacKlika.current,
        });
    }, [ruta]);

    /* Crtaju se samo kad je sloj upaljen. Upozorenja DUZ RUTE su namerno
       sklonjena odavde: predvidjanje sta ceka vozilo pripada planiranju rute,
       gde se ruta i zadaje, a ne izboru vozila na mapi. Endpoint
       `/gps/incidentiRute` ostaje i ceka taj ekran. */
    /* Dok je planiranje otvoreno, crtaju se prepreke SA SVIH predlozenih trasa,
       ne samo sa izabrane.

       Razlog je sama svrha predloga: bira se poredjenjem, a poredi se ono sto
       se VIDI. Ako se prikazu samo prepreke izabrane, druga trasa izgleda
       cista — pa ispadne da je bolja, a mozda je na njoj zatvoren put.

       Bez duplikata: ista blokada obicno lezi na vise trasa (preklapaju se na
       izlazu iz grada), a dva pina jedan preko drugog se ne mogu kliknuti. */
    const sviIncidenti = useMemo(() => {
        const po_id = new Map();

        if (planOtvoren) {
            /* Izabrana ide PRVA, pa njeni zapisi preziv­e dedupliakciju i nose
               „koliko km od pocetka" bas za nju. */
            const redom = planIzabrana != null
                ? [planRute[planIzabrana], ...planRute.filter((_, i) => i !== planIzabrana)]
                : planRute;
            redom.filter(Boolean).forEach(r => {
                (r.incidenti || []).forEach(x => { if (!po_id.has(x.id)) po_id.set(x.id, x); });
            });
        }

        (prikaziIncidente ? incidenti : []).forEach(x => { if (!po_id.has(x.id)) po_id.set(x.id, x); });
        return [...po_id.values()];
    }, [incidenti, prikaziIncidente, planOtvoren, planIzabrana, planRute]);

    /* Crtaju se samo oznake koje su u vidnom polju.

       Mereno: sest vozila razbacanih po Srbiji, Madjarskoj, Slovackoj i Hrvatskoj
       daju 1 359 incidenata. Na ekranu ih nikad nema toliko — ostalo je bio DOM
       koji se premesta pri svakom zumiranju, a ne vidi se.

       Okvir se malo prosiruje (`pad`), da oznaka blizu ivice bude vec nacrtana
       kad se mapa pomeri za nekoliko piksela, umesto da iskoci. */
    const incidentiUVidiku = useMemo(() => {
        if (!vidnoPolje) return sviIncidenti;
        const okvir = vidnoPolje.pad(0.25);
        return sviIncidenti.filter(x => okvir.contains([x.lat, x.lng]));
    }, [sviIncidenti, vidnoPolje]);

    /* Pulsira se samo dok je zatvorenih puteva malo, i to se racuna nad VIDLJIVIMA.

       Tako se ponasanje poklopi sa svrhom: udaljen pogled na pola Evrope ima
       petsto zatvorenih puteva i nijedan ne treba da trepce, a kad se zumira na
       grad sa tri, ta tri trepcu — bas tamo gde se i gleda. */
    const pulsirajuBlokade = useMemo(
        () => incidentiUVidiku.filter(x => x.zatvoren).length <= PULS_NAJVISE,
        [incidentiUVidiku]
    );

    /* Link za izabrano vozilo — jedan izvor za obe radnje u izborniku, da
       „kopiraj" i "otvori" nikad ne pokazuju na različito.

       Celu rutu kad je odredište zadato, inače samo tačku. Kad ruta postoji,
       iz nje se uzima do devet međutačaka: bez njih Google računa svoju rutu i
       vozač dobije trasu koju niko nije izabrao. */
    const vezaGoogleVozila = useCallback(() => {
        if (voziloIzabrano?.lat == null) return null;
        const odakle = { lat: voziloIzabrano.lat, lng: voziloIzabrano.lng };
        if (planCilj?.lat == null) return googleLinkTacke(odakle);

        const trasa = planIzabrana != null ? planRute[planIzabrana] : null;
        return googleLinkRute([odakle, ...uzorakTrase(trasa?.geometrija), planCilj]);
    }, [voziloIzabrano, planCilj, planIzabrana, planRute]);

    const aktivniSloj = nadjiSloj(mapa.sloj);
    /* Gustina je raster sloj nad podlogom — radi nad svakom, pa nema uslova
       osim da je provajder uopste podesen. */
    const gustina = mapa.saobracaj && imaSaobracaj(saobracajKonfig) ? saobracajKonfig : null;

    /* Gustina radi nad svakom podlogom (raster sloj), pa je prekidac obican
       prekidac — bez grananja po tome sta je ispod. */
    const prebaciSaobracaj = useCallback(() => {
        if (!imaSaobracaj(saobracajKonfig)) return;
        promeniMapu({ ...mapa, saobracaj: !mapa.saobracaj });
    }, [mapa, promeniMapu, saobracajKonfig]);

    /* Incidenti TAMO GDE SU VOZILA — nigde drugde.

       Bez izabranog vozila gleda se cela flota; sa izabranim samo ono. Radovi
       u Poljskoj gde nemamo nikoga su cist sum na ekranu, a placaju se isto
       kao i oni koji nesto znace.

       Trazi se samo dok je sloj upaljen — ugasen sloj ne sme da trosi. */
    useEffect(() => {
        if (!prikaziIncidente) { setIncidenti([]); return; }

        let ziv = true;
        zovi('/gps/incidenti', { mobilisis_id: izabrano || null, jezik: i18n.language })
            .then(d => {
                if (!ziv) return;
                setIncidenti(d?.incidenti || []);

                /* Kljuc nije podesen — sloj se gasi i kaze se zasto. Tiho
                   prazan sloj bi izgledao kao da na putevima nema nicega, sto
                   je gore od poruke da nije podesen. */
                if (d?.greska === 'NEMA_KLJUCA') {
                    setIncNedostupni(true);
                    setPrikaziIncidente(false);
                    toast.current?.show({
                        severity: 'warn', summary: t('common.upozorenje'),
                        detail: t('gps.incNijePodesen'), life: 6000,
                    });
                    return;
                }

                /* Vozilo koje se nikad nije javilo nema oko cega da se trazi. */
                if (d?.greska === 'NEMA_POZICIJE') {
                    toast.current?.show({
                        severity: 'info', summary: t('gps.incUkljuci'),
                        detail: t('gps.incNemaPozicije'), life: 4000,
                    });
                }
            })
            .catch(() => { if (ziv) setIncidenti([]); });
        return () => { ziv = false; };
    }, [prikaziIncidente, izabrano, zovi, i18n.language, t]);

    const postaviPin = useCallback((tacka) => {
        setPin(tacka);
        setKljucPina(k => k + 1);
    }, []);

    /* ---- merenje ----
       Jedan spisak tacaka pokriva i lenjir i „postavi kao pocetnu/zavrsnu
       tacku rute": oba su ista stvar — niz tacaka kroz koje se trazi kamionska
       ruta. Dve odvojene izvedbe bi znacile dve linije na mapi koje se ne
       slazu, a operater ne bi znao koja vazi. */
    const dodajTackuMerenja = useCallback((tacka, gde = 'KRAJ') => {
        setMerenje(m => {
            const stare = m?.tacke || [];
            const nove = gde === 'POCETAK' ? [tacka, ...stare] : [...stare, tacka];
            return { tacke: nove, rezultat: null, ucitava: false, poruka: null };
        });
    }, []);

    const ukloniTackuMerenja = useCallback((i) => {
        setMerenje(m => {
            if (!m) return m;
            const nove = m.tacke.filter((_, x) => x !== i);
            return { tacke: nove, rezultat: null, ucitava: false, poruka: null };
        });
    }, []);

    /* Ruta se racuna kad god se spisak promeni i ima bar dve tacke. Brojac
       cuva od zastarelog odgovora: dok ORS racuna staru rutu, operater vec ume
       da doda trecu tacku, pa bi stigao rezultat koji ne odgovara spisku. */
    const brojacMerenja = useRef(0);
    const kljucTacaka = (merenje?.tacke || []).map(x => `${x.lat},${x.lng}`).join('|');

    useEffect(() => {
        const tacke = merenje?.tacke || [];
        if (tacke.length < 2) return;

        const moj = ++brojacMerenja.current;
        setMerenje(m => (m ? { ...m, ucitava: true, poruka: null } : m));

        (async () => {
            try {
                const r = await zovi('/gps/udaljenost', { tacke });
                if (!ziv.current || brojacMerenja.current !== moj) return;
                setMerenje(m => (m ? { ...m, ucitava: false, rezultat: r, poruka: r?.poruka || null } : m));
            } catch (e) {
                if (!ziv.current || brojacMerenja.current !== moj) return;
                setMerenje(m => (m ? { ...m, ucitava: false, poruka: e.message } : m));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kljucTacaka, zovi]);

    /* ---- vozila u blizini ---- */
    const ucitajBlizinu = useCallback(async (lokacija, saRutama) => {
        setBlizina(b => ({ lokacija, vozila: b?.lokacija === lokacija ? b.vozila : [], ucitava: true, rute: saRutama, vazdusna: false }));
        try {
            const d = await zovi('/gps/blizu', { lat: lokacija.lat, lng: lokacija.lng, saRutama });
            if (!ziv.current) return;
            setBlizina({ lokacija, vozila: d?.vozila || [], ucitava: false, rute: saRutama, vazdusna: !!d?.vazdusna });
        } catch (e) {
            if (!ziv.current) return;
            setBlizina({ lokacija, vozila: [], ucitava: false, rute: saRutama, vazdusna: false });
            toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
        }
    }, [zovi, t]);

    /* ---- cuvanje lokacije ---- */
    const sacuvajLokaciju = useCallback(async (podaci) => {
        setCuvaLokaciju(true);
        try {
            const o = await zovi('/gps/lokacijaSnimi', podaci);
            if (!ziv.current) return;
            setDijalogLokacija(false);
            toast.current?.show({ severity: 'success', summary: t('common.sacuvano'), detail: o?.[0]?.message || t('gps.lokacijaSacuvana'), life: 3000 });
            ucitajLokacije();
            /* Pin ostaje, ali od sada zna da je sacuvan — dugme „dodaj" se gasi,
               inace bi se ista tacka upisala dvaput uzastopnim klikom. */
            setPin(p => (p ? { ...p, sacuvana: true } : p));
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
        } finally {
            if (ziv.current) setCuvaLokaciju(false);
        }
    }, [zovi, t, ucitajLokacije]);

    /* Klijent iz pretrage nema koordinate u bazi — vade se tek kad se izabere.
       Vraca null kad ni geokodiranje ne uspe, pa pozivalac zna da nema sta da
       pinuje i da mora da javi korisniku, umesto da tiho ne uradi nista. */
    const tackaIzKlijenta = useCallback(async (l) => {
        if (l.lat != null && l.lng != null) return { lat: l.lat, lng: l.lng };
        const g = await zovi('/gps/geokodiraj', { adresa: l.adresa, grad: l.grad, drzava: l.drzava });
        if (g?.lat == null) return null;
        return { lat: g.lat, lng: g.lng };
    }, [zovi]);

    /* ---- sta se desava kad se rezultat izabere ----
       Radnja se cita iz licnih podesavanja operatera; dok merenje traje, sve
       radnje ustupaju mesto dodavanju tacke, jer je to ono sto korisnik u tom
       trenutku radi. */
    const naIzborPretrage = useCallback(async (izvor, p) => {
        const uMerenju = !!merenje;

        /* --- VOZILO --- */
        if (izvor === IZVOR.VOZILO) {
            if (uMerenju) {
                dodajTackuMerenja({ lat: p.lat, lng: p.lng, naziv: p.registracija });
                return;
            }
            const akcija = podesavanja.akcije[IZVOR.VOZILO];
            if (akcija === 'PODESAVANJA') { history.push('/vozni_park'); return; }
            if (akcija === 'IZVESTAJ14')  { history.push('/gps/izvestaji', { mobilisis_id: p.mobilisis_id, dana: 14 }); return; }
            izaberiRef.current(p.mobilisis_id);
            return;
        }

        /* --- VOZAC --- */
        if (izvor === IZVOR.VOZAC) {
            if (uMerenju) {
                if (p.lat == null) {
                    toast.current?.show({ severity: 'warn', summary: t('gps.pretragaBezVozila'), life: 3000 });
                    return;
                }
                dodajTackuMerenja({ lat: p.lat, lng: p.lng, naziv: p.ime });
                return;
            }
            const akcija = podesavanja.akcije[IZVOR.VOZAC];

            if (akcija === 'CHAT') {
                if (!p.chat_id) {
                    toast.current?.show({ severity: 'warn', summary: t('common.upozorenje'), detail: t('gps.pretragaNemaNalog'), life: 4000 });
                    return;
                }
                history.push('/chat', { otvoriRazgovor: { id: p.chat_id, tip: p.chat_tip, ime: p.ime } });
                return;
            }
            if (akcija === 'PODESAVANJA') { history.push('/settings/configuration'); return; }

            /* Izvestaj i putanja postoje samo preko vozila — vozac bez naloga
               nema sta da se prikaze, pa se to i kaze umesto praznog ekrana. */
            if (!p.mobilisis_id) {
                toast.current?.show({ severity: 'warn', summary: t('common.upozorenje'), detail: t('gps.pretragaBezVozila'), life: 4000 });
                return;
            }
            history.push('/gps/izvestaji', { mobilisis_id: p.mobilisis_id, dana: 14 });
            return;
        }

        /* --- LOKACIJA (sacuvana ili klijent) --- */
        if (izvor === IZVOR.LOKACIJA) {
            const k = await tackaIzKlijenta(p);
            if (!k) {
                toast.current?.show({ severity: 'warn', summary: t('common.upozorenje'), detail: t('gps.pretragaNemaKoordinate'), life: 4000 });
                return;
            }
            const tacka = {
                ...k,
                naziv: p.naziv,
                opis: [p.adresa, p.grad, p.drzava].filter(Boolean).join(', '),
                adresa: p.adresa, grad: p.grad, drzava: p.drzava,
                id_klijent: p.id_klijent || null,
                tip: p.tip || null,
                sacuvana: p.izvor === 'SACUVANA',
            };
            if (uMerenju) { dodajTackuMerenja({ ...k, naziv: p.naziv }); return; }
            postaviPin(tacka);
            return;
        }

        /* --- ADRESA sa mape --- */
        if (izvor === IZVOR.ADRESA) {
            const tacka = {
                lat: p.lat, lng: p.lng,
                naziv: String(p.naziv || '').split(',')[0],
                opis: String(p.naziv || '').split(',').slice(1).join(',').trim(),
                adresa: String(p.naziv || '').split(',').slice(0, 2).join(',').trim(),
                grad: p.grad, drzava: p.drzava,
                sacuvana: false,
            };
            if (uMerenju) { dodajTackuMerenja({ lat: p.lat, lng: p.lng, naziv: tacka.naziv }); return; }

            const akcija = podesavanja.akcije[IZVOR.ADRESA];
            postaviPin(tacka);
            /* „Dodaj klijenta" kao podrazumevana radnja i dalje prvo pinuje —
               forma bez tacke na mapi iza sebe je unos naslepo. */
            if (akcija === 'KLIJENT' && can('GPS', 'edit')) setDijalogLokacija(true);
        }
    }, [merenje, podesavanja, history, dodajTackuMerenja, postaviPin, tackaIzKlijenta, can, t]);

    /* Klik na mapu dodaje tacku merenja. Van merenja mapa ostaje obicna mapa —
       klik koji uvek nesto dodaje pretvara pomeranje pogleda u niz slucajnih
       tacaka. */
    const naKlikMape = useCallback((lat, lng) => {
        dodajTackuMerenja({ lat, lng, naziv: `${lat.toFixed(4)}, ${lng.toFixed(4)}` });
    }, [dodajTackuMerenja]);

    /* Radnje sa kartice iznad pina. */
    const pinDodajLokaciju   = useCallback(() => setDijalogLokacija(true), []);
    const pinVozilaUBlizini  = useCallback(() => { if (pin) ucitajBlizinu(pin, false); }, [pin, ucitajBlizinu]);
    const pinMeri            = useCallback(() => {
        if (!pin) return;
        dodajTackuMerenja({ lat: pin.lat, lng: pin.lng, naziv: pin.naziv });
    }, [pin, dodajTackuMerenja]);
    const pinTackaRute       = useCallback((gde) => {
        if (!pin) return;
        dodajTackuMerenja({ lat: pin.lat, lng: pin.lng, naziv: pin.naziv }, gde);
    }, [pin, dodajTackuMerenja]);



    return (
        <div className="col-12 gps-stranica">
            <Toast ref={toast} />
            <Tooltip target=".gps-alatke button" position="bottom" />

            <div className="gps-omot">
                {/* Zaglavlje je tri zone: alatke levo, pretraga na sredini,
                    brojaci desno. Pretraga stoji u sredini jer je jedina stvar
                    kojoj se ovde prilazi sa namerom — alatke i brojaci se
                    koriste usput, dok se gleda mapa. */}
                <div className="gps-traka">
                    <div className="gps-alatke">
                        <Button
                            type="button"
                            icon="pi pi-refresh"
                            className="p-button-sm"
                            rounded
                            loading={ucitava}
                            onClick={() => { osveziPozicije(false); if (izabrano) ucitajRutu(izabrano); }}
                            data-pr-tooltip={t('gps.osvezi')}
                        />
                        <Button
                            type="button"
                            icon="pi pi-directions"
                            className="p-button-sm"
                            rounded
                            severity={prikaziTrag ? 'success' : 'secondary'}
                            onClick={() => setPrikaziTrag(p => !p)}
                            data-pr-tooltip={t('gps.tragSati', { sati: TRAG_MINUTA / 60 })}
                        />

                        {/* Zvonce — precrtano dok je sve ugašeno, pa se stanje vidi i bez
                            boje. Klik otvara izbor vrsta; dok alarmi kruže, dugme
                            pulsira samo u fazi prikaza. */}
                        <Button
                            type="button"
                            icon={ukljuceneVrste ? 'pi pi-bell' : 'pi pi-bell-slash'}
                            className={`p-button-sm gps-dugme-alarm${prikazAlarma ? ' gps-dugme-alarm-aktivan' : ''}`}
                            rounded
                            severity={ukljuceneVrste ? 'danger' : 'secondary'}
                            onClick={() => setDijalogAlarma(true)}
                            data-pr-tooltip={t('gps.alarmi')}
                        />

                        {izabrano && (
                            <Button
                                type="button"
                                icon="pi pi-times"
                                className="p-button-sm"
                                rounded
                                severity="secondary"
                                onClick={() => izaberi(null)}
                                data-pr-tooltip={t('gps.prikaziSve')}
                            />
                        )}
                    </div>

                    <PretragaGlavna
                        podesavanja={podesavanja}
                        vozila={vozila}
                        ucitavaMapu={ucitava}
                        /* Pretraga se naginje ka izabranom vozilu, a kad ga nema —
                            ka prvom vozilu flote. Bolje i to nego nista: bez ikakve
                            tacke TomTom za srpske firme ume da vrati hrvatske. */
                        blizu={voziloIzabrano || vozila[0] || null}
                        zovi={zovi}
                        merenjeAktivno={!!merenje}
                        onIzbor={naIzborPretrage}
                        onOtvoriPodesavanja={() => setDijalogPodesavanja(true)}
                    />

                    <div className="gps-stanje">
                        {/* Čipovi nisu samo brojači nego PREKIDAČI: pale i gase tu
                            grupu na mapi. Oko ne razlikuje pouzdano „ima obrub" od
                            „nema obrub", pa stanje nosi i znak (oko / precrtano oko)
                            i ispranost — tri razlike za istu stvar, da se pročita i
                            krajičkom oka i na lošem ekranu.

                            `aria-pressed` govori isto čitačima ekrana, kojima ni boja
                            ni obrub ne znače ništa. */}
                        {STANJA_VOZILA.map(st => {
                            const upaljen = vidljivaStanja.includes(st.kljuc);
                            return (
                                <button
                                    key={st.kljuc}
                                    type="button"
                                    aria-pressed={upaljen}
                                    className={`gps-cip gps-cip-${st.kljuc}`
                                        + (upaljen ? ' gps-cip-upaljen' : ' gps-cip-ugasen')}
                                    onClick={() => prebaciStanje(st.kljuc)}
                                    title={t(upaljen ? 'gps.stanjeSakrij' : 'gps.stanjePrikazi')}
                                >
                                    <i className={upaljen ? 'pi pi-eye' : 'pi pi-eye-slash'} />
                                    {t(st.naziv)}: {brojac[st.kljuc]}
                                </button>
                            );
                        })}
                        {poslednje && (
                            <span className="gps-osvezeno">
                                {t('gps.osvezeno')}: {poslednje.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                        )}
                    </div>
                </div>

                {izabrano && ruta && (
                    <div className="gps-sazetak">
                        <strong>{ruta.sazetak?.registracija || voziloIzabrano?.registracija}</strong>
                        <span>{t('gps.rutaSati', { sati: RUTA_SATI })}</span>
                        <span>{t('gps.tacaka')}: {ruta.sazetak?.tacaka ?? 0}</span>
                        {ruta.sazetak?.maks_brzina != null && <span>{t('gps.maksBrzina')}: {ruta.sazetak.maks_brzina} km/h</span>}
                        {!!stajanja.length && (
                            <span className="gps-sazetak-stajanja">
                                {t('gps.stajanjaBroj', { broj: stajanja.length })}
                                {(() => {
                                    const dugih = stajanja.filter(x => x.duga).length;
                                    return dugih ? ` (${t('gps.stajanjaDugih', { broj: dugih })})` : '';
                                })()}
                            </span>
                        )}
                        {ruta.sazetak?.od && <span>{ruta.sazetak.od} — {ruta.sazetak.do}</span>}
                        {/* Bez ovoga korisnik ne zna da mu deo putanje nedostaje. */}
                        {ruta.sazetak?.prekida > 0 && (
                            <span className="gps-upozorenje">
                                {t('gps.prekida', { broj: ruta.sazetak.prekida })}
                            </span>
                        )}
                        {(!ruta.tacke || ruta.tacke.length < 2) && (
                            <span className="gps-upozorenje">{t('gps.nemaRute')}</span>
                        )}
                    </div>
                )}

                <div className={`gps-mapa${ceoEkran ? ' gps-mapa-ceo' : ''}`} ref={okvirMape}>
                    <MapContainer ref={mapaRef} center={CENTAR_SRBIJA} zoom={7} scrollWheelZoom attributionControl={false} style={{ height: '100%', width: '100%' }}>
                        {/* Slojevi za trase plana prave se PRE nego što ijedna trasa
                            postoji. Leaflet vezuje liniju za sloj u trenutku stvaranja i
                            pukne ako sloja nema, a React pokreće efekte redom kojim su
                            deca napisana — pa red ovde nije stvar ukusa. */}
                        <SlojeviPlana />

                        {/* Kljuc je oznaka sloja: `url` se u react-leaflet-u menja u letu,
                            ali `maxZoom` ne — bez remontiranja bi satelit nasledio
                            granicu zuma prethodne podloge. */}
                        <TileLayer
                            key={aktivniSloj.kljuc}
                            url={aktivniSloj.url}
                            maxZoom={aktivniSloj.maxZoom}
                        />

                        {/* Saobracaj je PROZIRAN sloj IZNAD podloge, ne zamena za nju —
                            dispecer mora da vidi i put i gustinu na njemu. Providnost
                            je 0.9 da se ispod obojene linije jos nazire naziv ulice. */}
                        {!!gustina && (
                            <TileLayer
                                key={`saobracaj-${gustina.kljuc}`}
                                url={gustina.url}
                                /* 0.9, ne 0.75: providnost se MNOZI sa bojom plocice,
                                   pa je cetvrtina boje odlazila pre nego sto se linija
                                   uopste vidi. Ostavljeno malo ispod pune, taman da se
                                   ispod obojene linije jos nazire naziv ulice. */
                                opacity={0.9}

                                /* ── STEDNJA POZIVA ────────────────────────────────
                                   Svaka plocica je jedan zahtev ka TomTom-u, a sloj se
                                   povlaci iznova pri svakom pomeranju i zumiranju.

                                   `updateWhenIdle` ceka da se pomeranje ZAVRSI umesto da
                                   povlaci plocice kroz ceo prevlak — dispecer koji
                                   prevuce mapu preko pola Evrope inace plati svaki
                                   medjukorak, a nijedan nije ni pogledao.

                                   `updateWhenZooming={false}` isto to za zumiranje.

                                   `maxNativeZoom` staje na 13: iznad toga se uvecava
                                   poslednja povucena plocica umesto da se traze nove.
                                   Gustina saobracaja na ulici od dvesta metara ionako
                                   nije podatak zbog kog se ovaj sloj pali. */
                                updateWhenIdle
                                updateWhenZooming={false}
                                maxNativeZoom={13}
                                zIndex={350}
                            />
                        )}

                        {/* Kratki tragovi svih vozila — samo kad nijedno nije izabrano. */}
                        {!izabrano && linijeTragova.map(([id, tacke]) => (
                            <Polyline key={`trag-${id}`} positions={tacke} pathOptions={{ color: '#1976d2', weight: 3, opacity: 0.55 }} />
                        ))}

                        {/* Cela ruta izabranog vozila, isecena na segmente. */}
                        {izabrano && segmenti.map((s, i) => (
                            <Polyline
                                key={`ruta-${i}`}
                                positions={s}
                                pathOptions={{ color: '#1565c0', weight: 4, opacity: 0.85 }}
                            />
                        ))}

                        {/* Rupe u pracenju — isprekidano, da se ne citaju kao predjeni put. */}
                        {izabrano && rupe.map((s, i) => (
                            <Polyline
                                key={`rupa-${i}`}
                                positions={s}
                                pathOptions={{ color: '#9e9e9e', weight: 2, opacity: 0.65, dashArray: '6 8' }}
                            />
                        ))}

                        {zaPrikaz.map(v => (
                            <Marker
                                key={v.mobilisis_id}
                                position={[v.lat, v.lng]}
                                icon={ikonaVozila(
                                    v,
                                    v.mobilisis_id === izabrano,
                                    alarmiranaVozila.get(v.mobilisis_id),
                                    alarmiPoTablici.get(golaReg(partnerVozila(v)))
                                )}
                                eventHandlers={{
                                    click: () => izaberi(v.mobilisis_id),
                                    /* Dvoklik otvara planiranje. `stop` je obavezan: bez
                                        njega Leaflet na dvoklik zumira mapu, pa bi se
                                        prozor otvorio uz nezeljen skok pogleda. */
                                    dblclick: (e) => {
                                        L.DomEvent.stop(e);
                                        izaberi(v.mobilisis_id);
                                        setPlanOtvoren(true);
                                    },
                                }}
                            >
                                <MapTooltip direction="top" offset={[0, -14]}>
                                    <div className="gps-oblacic">
                                        <strong>{v.registracija}</strong>
                                        {/* Vrsta se ispisuje samo za prikolice — kod vucnog vozila
                                            je podrazumevana i samo bi trosila red. */}
                                        {v.vrsta === 'POLUPRIKOLICA' && <div className="gps-vrsta">{t('gps.poluprikolica')}</div>}
                                        {v.vrsta === 'PRIKOLICA' && <div className="gps-vrsta">{t('gps.prikolica')}</div>}
                                        {/* Tablice sprege stoje na samoj oznaci, pa se ovde ne
                                            ponavljaju — ostaje samo ono sto oznaka ne moze da
                                            kaze: da sprega potice sa poslednjeg naloga. */}
                                        {partnerVozila(v) && !v.nalog_aktivan && (
                                            <div className="gps-par"><em>{t('gps.spregaPoslednjiNalog')}</em></div>
                                        )}
                                        {/* Ime sa taho kartice je dokaz da je covek za
                                            volanom. Kad kartice nema, pise se ko je po
                                            putnom nalogu zaduzio vozilo — ali OZNACENO,
                                            jer je nalog raspored, a ne potvrda. */}
                                        {v.vozac
                                            ? <div>{v.vozac}</div>
                                            : v.vozac_nalog
                                                ? <div>{v.vozac_nalog} <em className="gps-izvor">({t('gps.poNalogu')})</em></div>
                                                : null}
                                        <div>{t('gps.brzina')}: {v.brzina ?? 0} km/h</div>
                                        {v.km != null && <div>{t('gps.kilometraza')}: {v.km.toLocaleString('sr-RS')}</div>}
                                        <div>{v.vreme}</div>
                                        {(v.minuta_staro ?? 0) > MINUTA_ZASTARELO && (
                                            <div className="gps-upozorenje">
                                                {t('gps.bezSignalaMin', { minuta: v.minuta_staro })}
                                            </div>
                                        )}
                                    </div>
                                </MapTooltip>
                            </Marker>
                        ))}


                        {/* Nevidljiva siroka linija preko rute — ona hvata mis.

                            Sama ruta je debela 4px i pogoditi je misem je muka; ovde je
                            18px i providna, pa se meta lako pogadja a izgled se ne menja.
                            Oznake vozila i stajanja su u `markerPane`, iznad linija, pa
                            im ovo ne otima klik. */}
                        {izabrano && segmenti.map((seg, i) => (
                            <Polyline
                                key={`meta-${i}`}
                                positions={seg}
                                pathOptions={{ color: '#000', weight: 18, opacity: 0 }}
                                /* Oblacic se vezuje ODMAH, prazan. Da se vezuje tek kad
                                    ima sta da pokaze, Leaflet ga ne bi ni otvorio — otvara
                                    ga na `mouseover`, a taj je do tada vec prosao.
                                    `sticky` ga drzi uz mis duz cele linije, umesto na
                                    njenoj sredini. */
                                ref={(sloj) => {
                                    if (sloj && !sloj.getTooltip()) {
                                        sloj.bindTooltip('', {
                                            sticky: true,
                                            direction: 'top',
                                            offset: [0, -6],
                                            opacity: 1,
                                        });
                                    }
                                }}
                                eventHandlers={{
                                    /* I `mouseover`, ne samo `mousemove`: Leaflet otvara
                                        oblacic bas na `mouseover`, pa bi bez ovoga prvi
                                        kadar bio prazan mehuric. */
                                    mouseover: naMisPoRuti,
                                    mousemove: naMisPoRuti,
                                    click: naKlikRute,
                                }}
                            />
                        ))}

                        {/* Kartica deonice. Nevidljiva oznaka na mestu klika samo nosi
                            popup — Leaflet popup mora da visi o necemu, a prava oznaka
                            bi na ruti bila jos jedna tacka koja zbunjuje. */}
                        {segmentKlik && (
                            <Marker
                                key={`seg-${segmentKlik.kljuc}`}
                                position={segmentKlik.gde}
                                icon={IKONA_NEVIDLJIVA}
                                ref={(m) => { if (m) m.openPopup(); }}
                            >
                                <Popup
                                    className="gps-pin-popup"
                                    minWidth={330}
                                    maxWidth={380}
                                    autoPan
                                    autoPanPadding={[24, 90]}
                                    closeButton
                                    /* Ciscenje samo ako je u medjuvremenu nije otvoren
                                        NOVI popup: Leaflet `remove` javlja i kad se stari
                                        zatvara zato sto ga novi zamenjuje, pa bi bezuslovno
                                        nulovanje ugasilo taj novi. */
                                    eventHandlers={{
                                        remove: () => setSegmentKlik(
                                            (p) => (p && p.kljuc === segmentKlik.kljuc ? null : p)
                                        ),
                                    }}
                                >
                                    <PopupSegment
                                        segment={segmentKlik}
                                        vozilo={voziloIzabrano}
                                        zovi={zovi}
                                    />
                                </Popup>
                            </Marker>
                        )}

                        {/* Predlozi trasa iz planiranja. Izabrana je puna i deblja,
                            ostale tanje i bledje — da se vidi sta je izabrano bez
                            gledanja u prozor. Isprekidane su, jer je ovo PLAN, a
                            predjeni put je puna linija.

                            Crtaju se dok je planiranje otvoreno — dakle i kad je
                            prozor sklonjen u traku, jer je mapa tada jedino mesto
                            gde se plan vidi. Zato svaka nosi oblacic sa trajanjem,
                            kilometrima i vremenom dolaska, pa se izbor moze
                            napraviti i bez prozora, klikom na samu trasu. */}
                        {planOtvoren && planRute.map((_, i) => {
                            const r = planRute[i];
                            if (!r?.geometrija || r.geometrija.length < 2) return null;
                            const izabrana = i === planIzabrana;
                            /* Broj stoji na SREDINI trase — tamo se tri linije najvise
                                razilaze, pa se brojevi ne poklope jedan preko drugog. */
                            const sredina = r.geometrija[Math.floor(r.geometrija.length / 2)];
                            return (
                                /* Ključ nosi i to da li je trasa izabrana, pa promena
                                    izbora MONTIRA liniju iznova.

                                    Bez toga se ne bi preselila u drugi sloj: Leaflet
                                    `pane` se određuje pri stvaranju sloja i kasnije se
                                    ne može promeniti — `setStyle` ga ignoriše. Linija
                                    bi ostala u sloju u kom je nastala, sa novom bojom
                                    ali starom visinom. */
                                <React.Fragment key={`plan-${r.id}-${izabrana ? 'i' : 's'}`}>
                                    {/* BELI OBRUB ispod linije.

                                        Bez njega su alternative bile praktično
                                        nevidljive: siva na 55% providnosti preko
                                        mape sa gradovima, autoputevima i desetinama
                                        oznaka radova nema za šta da se uhvati — pa
                                        se videla samo izabrana.

                                        Obrub je isti postupak koji koriste i Google
                                        i sama Leaflet mapa za puteve: linija se crta
                                        dvaput, deblja bela ispod i tanja u boji
                                        iznad. Time boja linije više ne mora da se
                                        bori sa pozadinom, pa alternative mogu ostati
                                        tihe a da i dalje budu čitljive. */}
                                    <Polyline
                                        positions={r.geometrija}
                                        pathOptions={izabrana
                                            ? { color: '#ffffff', weight: 11, opacity: 0.95, pane: SLOJ_IZABRANA }
                                            : { color: '#ffffff', weight: 8, opacity: 0.9, dashArray: '10 7', pane: SLOJ_SPOREDNE }}
                                        interactive={false}
                                    />
                                    <Polyline
                                        positions={r.geometrija}
                                        /* IZABRANA JE PUNA, alternative isprekidane.

                                            Ovo nije ukras nego jedini način da se izabrana
                                            vidi. Trase se na dugim deonicama poklapaju —
                                            iste autoputeve dele satima i razilaze se tek
                                            oko gradova. Dok je i izabrana bila
                                            isprekidana, siva ispod nje PROVIRIVALA JE KROZ
                                            NJENE PRAZNINE, pa je zajednička deonica
                                            izgledala sivo sa zelenim mrljama — bez obzira
                                            što se izabrana crta poslednja i što je iznad.
                                            Redosled crtanja ne pomaže kad je ono što je
                                            gore providno između crtica.

                                            Puna linija nema praznine, pa na poklopljenoj
                                            deonici pokriva sve ispod sebe. Crtice ostaju
                                            alternativama, gde uz to nose i značenje
                                            „mogućnost, ne odluka". */
                                        pathOptions={izabrana
                                            ? { color: '#00897b', weight: 6, opacity: 1, pane: SLOJ_IZABRANA }
                                            : { color: '#90a4ae', weight: 4, opacity: 1, dashArray: '10 7', pane: SLOJ_SPOREDNE }}
                                        eventHandlers={{ click: () => setPlanIzabrana(i) }}
                                    />
                                    <Marker
                                        position={sredina}
                                        icon={ikonaTrase(i + 1, izabrana, r.minuta, r.km)}
                                        /* Razlika mora biti VELIKA, ne simbolična.

                                            Leaflet računa z-index markera kao
                                            `y-piksel + zIndexOffset`, pa je raniji par
                                            420/400 bio besmislen: dve trase razmaknute
                                            po visini ekrana razlikuju se po `y` za
                                            stotine piksela, što razliku od 20 briše.
                                            Zato je južniji oblačić prelazio preko
                                            izabranog.

                                            2000 je iznad visine svakog ekrana, pa nadjača
                                            `y` bez obzira gde se trase nalaze. 400 za
                                            ostale i dalje drži oblačiće iznad oznaka
                                            radova (300). */
                                        zIndexOffset={izabrana ? 2000 : 400}
                                        eventHandlers={{ click: () => setPlanIzabrana(i) }}
                                    />
                                </React.Fragment>
                            );
                        })}

                        {/* Radovi, blokade, nezgode. Idu IZNAD rute i stajanja, a ispod
                            vozila: to je ono sto vozilo tek ceka, pa ne sme da prekrije
                            samo vozilo. */}
                        {/* Kartica se NE kači na svaki marker nego se pravi tek za
                            onaj koji je kliknut.

                            Razlog je cena: `<Popup>` u React-Leaflet-u iscrtava svoju
                            decu odmah po montiranju, i kad je zatvoren. Jedna ćelija
                            oko Beograda vraća blizu dvesta incidenata, a cela flota
                            preko hiljadu — toliko puta se pravilo celo stablo kartice
                            i toliko Leaflet popup objekata, samo da bi se video jedan.

                            Isti postupak već koristi kartica deonice rute. */}
                        {incidentiUVidiku.map(inc => (
                            <Marker
                                key={`inc-${inc.id}`}
                                position={[inc.lat, inc.lng]}
                                icon={ikonaIncidenta(inc.vrsta, inc.zatvoren, pulsirajuBlokade)}
                                zIndexOffset={300}
                                eventHandlers={{
                                    /* Osnovni podaci na prelazak, detalji na klik. */
                                    mouseover: (e) => veziOblacic(e.target, inc, t),
                                    click: () => setIncidentKlik({
                                        incident: inc,
                                        kljuc: ++brojacIncKlika.current,
                                    }),
                                }}
                            />
                        ))}

                        {/* Deonica na koju se otvorena oznaka odnosi, u njenoj boji.
                            Kod raskrsnice se iz same oznake ne zna koja je od tri
                            ulice u pitanju — linija to kaže. */}
                        {incidentKlik?.incident?.linija?.length > 1 &&
                            TRAGOVI_DEONICE(vrstaOpis(incidentKlik.incident.vrsta).boja).map((stil, i) => (
                                <Polyline
                                    key={`deon-${incidentKlik.kljuc}-${i}`}
                                    positions={incidentKlik.incident.linija}
                                    pathOptions={stil}
                                />
                            ))}

                        {incidentKlik && (
                            <Marker
                                key={`inck-${incidentKlik.kljuc}`}
                                position={[incidentKlik.incident.lat, incidentKlik.incident.lng]}
                                icon={IKONA_NEVIDLJIVA}
                                ref={(m) => { if (m) m.openPopup(); }}
                            >
                                <Popup
                                    className="gps-pin-popup"
                                    minWidth={300}
                                    maxWidth={340}
                                    autoPan
                                    autoPanPadding={[24, 90]}
                                    closeButton
                                    /* Ciscenje samo ako u medjuvremenu nije otvoren NOVI
                                        popup — Leaflet `remove` javlja i kad se stari
                                        zatvara zato sto ga novi zamenjuje. */
                                    eventHandlers={{
                                        remove: () => setIncidentKlik(
                                            (p) => (p && p.kljuc === incidentKlik.kljuc ? null : p)
                                        ),
                                    }}
                                >
                                    <PopupIncident incident={incidentKlik.incident} />
                                </Popup>
                            </Marker>
                        )}

                        {/* Smer kretanja. Strelice ne hvataju klik (`interactive:false`
                            u ikoni) — inace se preko njih ne bi moglo ni kliknuti na
                            rutu ni prevuci mapa. */}
                        {izabrano && strelice.map((s, i) => (
                            <Marker
                                key={`str-${i}`}
                                position={s.tacka}
                                icon={IKONA_STRELICA(s.ugao)}
                                interactive={false}
                                keyboard={false}
                            />
                        ))}

                        {/* Gde je vozilo stajalo. Puna oznaka je pauza od 45 min ili
                            duze, suplja krace zadrzavanje — razlika je u POPUNJENOSTI,
                            a ne u boji ili velicini, jer je to isti dogadjaj razlicitog
                            trajanja i mora da se cita kao jedna vrsta. */}
                        {izabrano && stajanja.map(st => (
                            <Marker
                                key={`staj-${st.redni}`}
                                position={[st.lat, st.lng]}
                                icon={ikonaStajanja(st.sekundi)}
                                zIndexOffset={200}
                            >
                                <Popup className="gps-pin-popup" minWidth={300} maxWidth={340} autoPan autoPanPadding={[24, 90]} closeButton>
                                    <PopupStajanje
                                        stajanje={st}
                                        vozilo={voziloIzabrano}
                                        sektor={detalji?.mobilisis?.sektor}
                                        zovi={zovi}
                                    />
                                </Popup>

                                <MapTooltip direction="top" offset={[0, -10]}>
                                    <div className="gps-oblacic">
                                        <strong>{trajanjeTeksta(st.sekundi)}</strong>
                                        <div>{st.od}</div>
                                    </div>
                                </MapTooltip>
                            </Marker>
                        ))}

                        {/* ---- glavna pretraga: sloj nad vozilima ---- */}

                        <KlikNaMapu aktivno={!!merenje} onKlik={naKlikMape} />

                        {/* Sacuvana mesta stoje uvek, i kad se nista ne trazi —
                            dispecer po njima cita mapu isto kao po vozilima. */}
                        {lokacije.map(l => (
                            <Marker key={`lok-${l.id}`} position={[l.lat, l.lng]} icon={IKONA_LOKACIJA}>
                                <MapTooltip direction="top" offset={[0, -10]}>
                                    <div className="gps-oblacic">
                                        <strong>{l.naziv}</strong>
                                        {!!l.tip_opis && <div className="gps-vrsta">{l.tip_opis}</div>}
                                        {!!l.adresa && <div>{l.adresa}</div>}
                                        {!!l.grad && <div>{[l.grad, l.drzava].filter(Boolean).join(', ')}</div>}
                                    </div>
                                </MapTooltip>
                            </Marker>
                        ))}

                        {/* Rute do vozila u blizini — svaka svojom bojom, istom
                            koju red ima u tabeli, pa se linija i red spajaju
                            pogledom umesto citanjem registracije. */}
                        {blizina?.rute && (blizina.vozila || []).map((v, i) => (
                            v.geometrija && v.geometrija.length > 1 ? (
                                <Polyline
                                    key={`blizu-${v.mobilisis_id}`}
                                    positions={v.geometrija}
                                    pathOptions={{ color: BOJE_RUTA[i % BOJE_RUTA.length], weight: 3, opacity: 0.8 }}
                                />
                            ) : null
                        ))}

                        {/* Izmerena ruta. Isprekidana je namerno: ne sme da se
                            pomesa sa predjenim putem vozila, koji je pun podatak
                            iz uredjaja, dok je ovo predlog rutera. */}
                        {merenje?.rezultat?.geometrija?.length > 1 && (
                            <Polyline
                                positions={merenje.rezultat.geometrija}
                                pathOptions={{ color: '#d81b60', weight: 4, opacity: 0.9, dashArray: '10 6' }}
                            />
                        )}

                        {(merenje?.tacke || []).map((tc, i) => (
                            <Marker
                                key={`mer-${tc.lat}-${tc.lng}-${i}`}
                                position={[tc.lat, tc.lng]}
                                icon={ikonaMerenja(i + 1, i === 0, i === merenje.tacke.length - 1 && merenje.tacke.length > 1)}
                            >
                                <MapTooltip direction="top" offset={[0, -12]}>
                                    <div className="gps-oblacic"><strong>{nazivTacke(tc)}</strong></div>
                                </MapTooltip>
                            </Marker>
                        ))}

                        {/* Pin pretrage ide POSLEDNJI da bi bio iznad svega —
                            to je jedina tacka koju je korisnik izricito trazio. */}
                        {pin && (
                            /* Kljuc je brojac skoka, pa se marker REMONTIRA na svaki nov
                               pin — samo tako se kartica otvori sama. Leaflet popup se
                               inace otvara tek na klik, a tacka koju je korisnik upravo
                               izabrao ne sme da trazi jos jedan klik da bi progovorila. */
                            <Marker
                                key={`pin-${kljucPina}`}
                                position={[pin.lat, pin.lng]}
                                icon={IKONA_PIN}
                                ref={(m) => { if (m) m.openPopup(); }}
                            >
                                <Popup
                                    className="gps-pin-popup"
                                    minWidth={280}
                                    maxWidth={320}
                                    closeButton
                                    autoPan
                                    /* Bez razmaka kartica zna da zavrsi pod zaglavljem mape,
                                       pa se naslov ne vidi. */
                                    autoPanPadding={[24, 90]}
                                >
                                    <PopupPin
                                        tacka={pin}
                                        mozeUpis={can('GPS', 'edit') && !pin.sacuvana}
                                        onDodajLokaciju={pinDodajLokaciju}
                                        onVozilaUBlizini={pinVozilaUBlizini}
                                        onMeri={pinMeri}
                                        onTackaRute={pinTackaRute}
                                    />
                                </Popup>
                            </Marker>
                        )}

                        <PomeriNaTacku tacka={pin} kljucSkoka={kljucPina} />

                        {/* Zastavica na cilju. Ide POSLE trasa, pa stoji iznad njih, i
                            nosi naziv na prelazak mišem — pin bez imena kaže samo „negde
                            ovde", a odredište je često firma čije se ime ne vidi nigde
                            drugde na mapi. */}
                        {planOtvoren && planCilj?.lat != null && (
                            <Marker
                                position={[planCilj.lat, planCilj.lng]}
                                icon={ikonaOdredista()}
                                zIndexOffset={2100}
                            >
                                <MapTooltip direction="top" offset={[0, -30]}>
                                    {planCilj.naziv}
                                </MapTooltip>
                            </Marker>
                        )}

                        {/* Prati vidno polje samo dok su oznake upaljene — bez njih
                            nema sta da se odseca, pa ni razloga za osluskivanje. */}
                        {prikaziIncidente && <PratiOkvir onOkvir={setVidnoPolje} />}

                        {prikaziTacke && (
                            <SlojTacaka
                                tacke={tacke}
                                sedista={sedistaFirmi}
                                vidljiviTipovi={vidljiviTipovi}
                                prikaziSedista={!sedistaUgasena}
                                t={t}
                            />
                        )}

                        {planOtvoren && <UklopiTrase rute={planRute} kljuc={planKljuc} />}

                        <DugmeCeoEkran
                            ceoEkran={ceoEkran}
                            onPrebaci={prebaciCeoEkran}
                            naslov={ceoEkran ? t('gps.izadjiCeoEkran') : t('gps.ceoEkran')}
                        />
                        {/* Poredak u JSX-u = poredak na ekranu (Leaflet slaze kontrole
                            redom dodavanja): +/-, ceo ekran, podloga, saobracaj. */}
                        <DugmeKontrole
                            ikona={`<i class="${aktivniSloj.ikona}"></i>`}
                            naslov={t('gps.slojIzaberi')}
                            aktivno={izbornikSlojeva}
                            onKlik={() => setIzbornikSlojeva(o => !o)}
                        />

                        {/* Dva automobila = gustina saobracaja. Kad provajder nije
                            podesen, dugme STOJI ali je ugaseno: sakriveno bi znacilo
                            „ne postoji", a ovako se vidi da postoji i sta mu fali. */}
                        <DugmeKontrole
                            ikona={IKONA_SAOBRACAJ}
                            naslov={imaSaobracaj(saobracajKonfig)
                                ? (mapa.saobracaj ? t('gps.saobracajIskljuci') : t('gps.saobracajUkljuci'))
                                : t('gps.saobracajNijePodesen')}
                            aktivno={mapa.saobracaj && imaSaobracaj(saobracajKonfig)}
                            iskljuceno={!imaSaobracaj(saobracajKonfig)}
                            onKlik={prebaciSaobracaj}
                        />

                        {/* Jedini ulaz u Google Maps koji ne trazi preduslov —
                            ostali postoje tek kad se nesto izabere (pin, merenje,
                            vozilo). Deep link: ne trosi kvotu i radi bez kljuca. */}
                        {/* Radovi i blokade su DRUGI podatak od gustine, pa i drugo
                            dugme. Gustina je slika po putevima, ovo su dogadjaji nad
                            kojima se moze kliknuti. */}
                        {/* Tačke su statičan podatak (pumpe, carine, sedišta), za
                            razliku od vozila koja se pomeraju — zato svoj prekidač. */}
                        <DugmeKontrole
                            ikona={`<i class="pi pi-map-marker"></i>`}
                            naslov={t(prikaziTacke ? 'tacke.sakrijSloj' : 'tacke.prikaziSloj')}
                            aktivno={prikaziTacke}
                            onKlik={() => setPrikaziTacke(o => !o)}
                        />

                        <DugmeKontrole
                            ikona={`<i class="pi pi-exclamation-triangle"></i>`}
                            naslov={incNedostupni
                                ? t('gps.incNijePodesen')
                                : t(prikaziIncidente ? 'gps.incIskljuci' : 'gps.incUkljuci')}
                            aktivno={prikaziIncidente}
                            iskljuceno={incNedostupni}
                            onKlik={() => setPrikaziIncidente(o => !o)}
                        />

                        {/* Radi TEK kad je vozilo izabrano: obe radnje ispod
                            („gde je" i „kuda ide") su o konkretnom vozilu, pa bez
                            njega dugme nema na sta da se primeni. Ne sakriva se
                            nego gasi — sakriveno bi znacilo „ne postoji". */}
                        <DugmeKontrole
                            ikona={IKONA_GOOGLE_MAPS}
                            naslov={voziloIzabrano ? t('gps.googleVozilo') : t('gps.googleTrebaVozilo')}
                            aktivno={izbornikGoogle}
                            iskljuceno={!voziloIzabrano}
                            onKlik={() => setIzbornikGoogle(o => !o)}
                        />

                        <OsveziVelicinu okidac={ceoEkran} />

                        <PomeriNaVozilo vozilo={voziloIzabrano} kljucSkoka={kljucSkoka} />
                    </MapContainer>

                    {/* Spisak tipova je i legenda i prekidač: boja pina stoji uz
                        naziv, pa se mapa čita bez pogađanja šta koja boja znači.
                        Vidi se samo dok je sloj upaljen — inače bi zauzimao mesto
                        zbog nečega što se ne prikazuje. */}
                    {prikaziTacke && (tipoviNaMapi.length > 0 || sedistaFirmi.length > 0) && (
                        <div className="gps-tacke-legenda" role="group"
                             aria-label={t('tacke.sloj')}>
                            {sedistaFirmi.length > 0 && (
                                <button type="button"
                                        className={`gps-tacke-stavka${sedistaUgasena ? ' ugaseno' : ''}`}
                                        onClick={() => setSedistaUgasena(o => !o)}>
                                    <span className="gps-tacke-boja" style={{ background: '#0b7285' }}>
                                        <i className="pi pi-home" />
                                    </span>
                                    {t('tacke.sedista')}
                                    <em>{sedistaFirmi.length}</em>
                                </button>
                            )}

                            {tipoviNaMapi.map(tip => {
                                const izgled = izgledTipa(tip.naziv);
                                const ugaseno = ugaseniTipovi.has(tip.kljuc);
                                return (
                                    <button key={tip.kljuc} type="button"
                                            className={`gps-tacke-stavka${ugaseno ? ' ugaseno' : ''}`}
                                            onClick={() => prebaciTip(tip.kljuc)}>
                                        <span className="gps-tacke-boja" style={{ background: izgled.boja }}>
                                            <i className={`pi ${izgled.ikona}`} />
                                        </span>
                                        {tip.naziv}
                                        <em>{tip.broj}</em>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Izbornik podloga stoji uz svoje dugme u levoj koloni, a ne
                        u dijalogu: bira se cesto i uz pogled na mapu, pa prozor
                        koji prekrije mapu ovde odmaze. */}
                    {izbornikSlojeva && (
                        <div className="gps-slojevi" role="menu">
                            {SLOJEVI.map(sl => {
                                const izabrana = sl.kljuc === mapa.sloj;
                                return (
                                    <button
                                        key={sl.kljuc}
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={izabrana}
                                        className={`gps-sloj${izabrana ? ' gps-sloj-aktivan' : ''}`}
                                        onClick={() => {
                                            setIzbornikSlojeva(false);
                                            if (izabrana) return;
                                            /* Gustina se ne dira pri promeni podloge — raster
                                               sloj radi nad svakom, pa nema razloga da se gasi. */
                                            promeniMapu({ ...mapa, sloj: sl.kljuc });
                                        }}
                                    >
                                        <i className={sl.ikona} />
                                        <span className="gps-sloj-tekst">
                                            <span className="gps-sloj-naziv">{t(sl.naziv)}</span>
                                            <span className="gps-sloj-opis">{t(sl.opis)}</span>
                                        </span>
                                        {izabrana && <i className="pi pi-check" />}
                                    </button>
                                );
                            })}

                            {/* Otvaranje u Google Maps NIJE podloga nego izlaz iz
                                aplikacije, pa stoji ispod crte. Radi uvek — obicna adresa,
                                bez kljuca i bez kvote. */}
                            <div className="gps-sloj-crta" />

                            <button
                                type="button"
                                role="menuitem"
                                className="gps-sloj gps-sloj-radnja"
                                onClick={() => {
                                    setIzbornikSlojeva(false);
                                    const m = mapaRef.current;
                                    if (!m) return;
                                    const c = m.getCenter();
                                    otvoriGoogle(googleLinkPrikaza(c.lat, c.lng, m.getZoom()));
                                }}
                            >
                                <i className="pi pi-external-link" />
                                <span className="gps-sloj-tekst">
                                    <span className="gps-sloj-naziv">{t('gps.otvoriPrikazUGoogle')}</span>
                                    <span className="gps-sloj-opis">{t('gps.otvoriPrikazUGoogleOpis')}</span>
                                </span>
                            </button>
                        </div>
                    )}

                    {/* Dve radnje nad istim linkom: poslati ga ili otvoriti.

                        Kopiranje je namerno PRVO — ruta se češće šalje vozaču u
                        poruci nego što se otvara ovde. Obe gađaju isti cilj, a
                        opis ispod naziva kaže koji: celu rutu kad je odredište
                        zadato, inače samo trenutnu poziciju. */}
                    {izbornikGoogle && !!voziloIzabrano && (
                        <div className="gps-slojevi gps-izbornik-google" role="menu">
                            <button
                                type="button"
                                role="menuitem"
                                className="gps-sloj gps-sloj-radnja"
                                onClick={async () => {
                                    setIzbornikGoogle(false);
                                    const uspelo = await kopirajLink(vezaGoogleVozila());
                                    toast.current?.show({
                                        severity: uspelo ? 'success' : 'warn',
                                        summary: t(uspelo ? 'gps.googleKopirano' : 'gps.googleNeuspelo'),
                                        life: 2500,
                                    });
                                }}
                            >
                                <i className="pi pi-copy" />
                                <span className="gps-sloj-tekst">
                                    <span className="gps-sloj-naziv">{t('gps.googleKopiraj')}</span>
                                    <span className="gps-sloj-opis">
                                        {t(planCilj ? 'gps.googleMetaRuta' : 'gps.googleMetaPozicija')}
                                    </span>
                                </span>
                            </button>

                            <button
                                type="button"
                                role="menuitem"
                                className="gps-sloj gps-sloj-radnja"
                                onClick={() => {
                                    setIzbornikGoogle(false);
                                    otvoriGoogle(vezaGoogleVozila());
                                }}
                            >
                                <i className="pi pi-external-link" />
                                <span className="gps-sloj-tekst">
                                    <span className="gps-sloj-naziv">{t('gps.googleOtvori')}</span>
                                    <span className="gps-sloj-opis">
                                        {t(planCilj ? 'gps.googleMetaRuta' : 'gps.googleMetaPozicija')}
                                    </span>
                                </span>
                            </button>
                        </div>
                    )}

                    {/* Esri i TomTom traze da im se izvor navede; Leaflet-ova
                        atribucija je iskljucena (zauzimala je red preko cele
                        sirine), pa stoji ovako — sitno, u uglu, i menja se sa
                        aktivnim slojem. */}
                    <div className="gps-atribucija">
                        {aktivniSloj.atribucija}
                        {!!gustina?.atribucija && ` · ${gustina.atribucija}`}
                    </div>

                    {/* Panel stoji NAD mapom, ne pored nje — mapa tako ne gubi sirinu
                        kad se panel otvori, pa se pogled ne pomera pod rukom. */}
                    {/* Panel alarma deli mesto sa panelom vozila. Izbor vozila je
                        izricita radnja korisnika, pa ima prednost nad alarmima. */}
                    {prikazAlarma && (
                        <PanelAlarmi
                            alarmi={alarmi}
                            /* X sklanja SAMO ovaj prikaz — alarmi ostaju uključeni i
                               spisak se vraća u sledećem krugu. Gasi se kroz dugme
                               sa zvoncetom, tamo gde je i upaljen. */
                            onZatvori={() => setSakrijPrikaz(true)}
                            naVozilo={(id) => izaberi(id)}
                        />
                    )}


                    {/* Merenje stoji dole levo — jedini slobodan ugao mape:
                        gore je zaglavlje, desno panel vozila, gore levo Leaflet
                        kontrole. */}
                    {merenje && (
                        <PanelMerenje
                            tacke={merenje.tacke}
                            rezultat={merenje.rezultat}
                            ucitava={merenje.ucitava}
                            poruka={merenje.poruka}
                            onUkloni={ukloniTackuMerenja}
                            onOcisti={() => setMerenje({ tacke: [], rezultat: null, ucitava: false, poruka: null })}
                            onZatvori={() => setMerenje(null)}
                        />
                    )}

                    <DialogPlanRute
                        vidljiv={planOtvoren}
                        vozilo={voziloIzabrano}
                        zovi={zovi}
                        onRute={(r) => { setPlanRute(r); setPlanKljuc(k => k + 1); }}
                        onIzabrana={setPlanIzabrana}
                        onOdrediste={setPlanCilj}
                        /* Zatvaranje briSe i trase: plan koji se ne vidi u prozoru,
                            a stoji na mapi, posle minut deluje kao predjeni put. */
                        onZatvori={() => { setPlanOtvoren(false); setPlanRute([]); setPlanIzabrana(null); setPlanCilj(null); }}
                    />

                    <DialogPodesavanjaPretrage
                        vidljiv={dijalogPodesavanja}
                        podesavanja={podesavanja}
                        cuva={cuvaPodesavanja}
                        onSacuvaj={sacuvajPodesavanja}
                        onZatvori={() => setDijalogPodesavanja(false)}
                    />

                    <DialogVozilaUBlizini
                        vidljiv={!!blizina}
                        lokacija={blizina?.lokacija}
                        vozila={blizina?.vozila}
                        ucitava={!!blizina?.ucitava}
                        vazdusna={!!blizina?.vazdusna}
                        prikaziRute={!!blizina?.rute}
                        onPrikaziRute={(v) => blizina && ucitajBlizinu(blizina.lokacija, v)}
                        onOsvezi={() => blizina && ucitajBlizinu(blizina.lokacija, blizina.rute)}
                        /* Klik na vozilo zatvara spisak i prebacuje na to vozilo —
                           spisak je bio pitanje, vozilo je odgovor, pa nema
                           razloga da oba ostanu na ekranu. */
                        onVozilo={(v) => { setBlizina(null); izaberi(v.mobilisis_id); }}
                        onZatvori={() => setBlizina(null)}
                    />

                    {pin && (
                        <DialogLokacija
                            vidljiv={dijalogLokacija}
                            tacka={pin}
                            tipovi={tipoviLokacije}
                            cuva={cuvaLokaciju}
                            onSacuvaj={sacuvajLokaciju}
                            onZatvori={() => setDijalogLokacija(false)}
                        />
                    )}

                    <DialogAlarmi
                        vidljiv={dijalogAlarma}
                        vrste={VRSTE_ALARMA}
                        izbor={izborAlarma}
                        onIzbor={(kljuc, vrednost) => setIzborAlarma(st => ({ ...st, [kljuc]: vrednost }))}
                        onZatvori={() => setDijalogAlarma(false)}
                    />

                    {izabrano && (
                        <PanelVozila
                            /* Dvoklik na vozilo je brz, ali se ne vidi. Dugme u panelu
                                je jedini ulaz koji se PRIMETI — ista pouka kao kod
                                „Otvori u Google Maps" (OP-59). */
                            onPlanRute={() => setPlanOtvoren(true)}
                            detalji={detalji}
                            ucitava={ucitavaDetalje}
                            stanje={voziloIzabrano ? stanjeVozila(voziloIzabrano) : 'stoji'}
                            alarmi={alarmiIzabranog}
                            onZatvori={() => izaberi(null)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default GPS;
